import {effect, ElementRef, Injectable, signal, untracked} from '@angular/core';
import cytoscape, {Core, EdgeSingular, NodeSingular} from 'cytoscape';
import {NodeService} from '../../api/services/node.service';
import {ModelService} from '../../api/services/model.service';
import {BehaviorSubject, concatMap, from, fromEvent, mergeMap, of, Subject} from 'rxjs';
import {SidebarService} from '../../sidebar-form/sidebar.service';
import {MapStateService} from '../../api/services/map-state.service';
import {DashboardComponent} from '../../dashboard/dashboard.component';
import {SettingsService} from '../../api/services/setting.service';
import {CyMaporiumServiceCore} from './cy-maporium-service.core';
import {DropCandidate, pickDropTarget} from './drop-target.util';
import {ButtonPressService} from '../button-press.service';
import {first, isEmpty, set} from 'lodash';
import {
  centeringInProgress,
  ExpandCollapseInProgress,
  invalidateGeneralPanZoom,
  invalidatePanZoomForCollapsedNode,
  restoreSavedPanZoomForCollapsedNode,
  savePanZoomState,
  setHomeButtonCentering,
  setPanZoomForCollapsedNode,
  setRestorationCompleted
} from '../../../cytoscape-extensions/pan-zoom-storage';
import {EdgeTypeMapCy, GraphMap, GraphNode, GraphNodeLink, LayoutType} from '../../api/interfaces/graph-node.interface';
import {isRestoringPanZoom, updateInProgress} from '../../shared/helpers/signal.helpers';
import {cytoscapeStrokeStyle} from '../../shared/helpers/cytoscape-dash.helper';
import {DefaultNodeProperties, DefaultValues, resolveImageScale, scaledCoverSize, strokeDashPattern, strokeLineCap} from '@maporium-workspace/shared';
import {MapService} from '../../api/services/map.service';
import {MapRoleService} from '../../acl/map-role.service';
import {Permission} from '../../acl/map-user-roles.enum';
import {GeneralInfo} from '../../api/interfaces/general-info';
import {miscStyles, nodeDimFactor, StatusIcons} from '../../dashboard/cy-styles';
import {transformColorWithHSL} from '../../shared/helpers/color-helpers';
import {isInIframe} from '../../shared/helpers/maporium.validators';
import {NodeStateData} from '../../../../../maporium/src/app/entities/types/node-state-data';
import {LinkStateData} from '../../../../../maporium/src/app/entities/types/link-state.data';
import {environment} from '../../../environments/environment';
import {CommentsStateManager} from '../../shared/components/comments/comments.state-manager';
import installHtmlEdgeLabels, {
  HtmlEdgeLabelsRenderer
} from '../../../cytoscape-extensions/maporium-cy-plugins/html-edge-labels';
import {GizmoOverlay} from '../../../cytoscape-extensions/maporium-cy-plugins/addons/gizmo-manager';
import {
  installTranslucentArrowheads
} from '../../../cytoscape-extensions/maporium-cy-plugins/translucent-arrowheads';
import {installNodeImageClip} from '../../../cytoscape-extensions/maporium-cy-plugins/node-image-clip';
import {
  ChangeContainerCommand,
  ContainerChangeSnapshot,
  CreateNodeCommand,
  EdgeAnchorCommand,
  EdgeControlPointSnapshot,
  GraphNodeSnapshot,
  MoveCommand,
  MoveSnapshot,
  UndoRedoManagerService
} from '../undo-redo';
import {cyNodeShape} from '../../shared/helpers/node-shape.helper';

@Injectable({
  providedIn: 'root'
})
export class CytoscapeService extends CyMaporiumServiceCore {
  public static menuChangedSubject = new Subject<void>();
  public linkingCompleteSubject = new BehaviorSubject(false);
  public readonly ZOOM_PERCENTAGE_CONTAINER = 0.75;
  public readonly ZOOM_PERCENTAGE_NODE = 0.25;
  public readonly ZOOM_PERCENTAGE_LINK = 0.85;

  private boxSelectionElements: any[] = [];
  private dragStarted = false;

  // MAP-1270: Flag to track shift-selection during grab (to skip tap toggle)
  public static shiftGrabOccurred = false;
  // MAP-1270: Store nodes that need to follow the grabbed node during shift-drag
  private static shiftDragFollowers: Map<string, { x: number; y: number }> = new Map();
  // Store edge control points captured on grab for undo/redo
  private static grabbedEdgeControlPoints: EdgeControlPointSnapshot[] = [];

  private static readonly SELECTION_RING_PX = 6;
  private static readonly SELECTION_RING_MIN_PX = 3;
  private static readonly SELECTION_GAP_PX = 1;
  private static readonly SELECTION_FOCUS_OPACITY = 0.85;
  private static readonly SELECTION_OTHERS_OPACITY = 0.65;
  private static readonly SELECTION_NODE_RING_SHARE = 0.15;
  private static readonly SELECTION_LINK_RING_SHARE = 1;
  private static readonly SELECTION_ZOOM_TOLERANCE = 0.08;
  private selectionLook = { zoom: 0, focusId: null as string | null, count: 0 };
  private selectionRefreshQueued = false;

  // Custom Alt-based box selection
  private isAltBoxSelecting = false;
  private altBoxStartPos: { x: number; y: number } | null = null;
  private altBoxElement: HTMLDivElement | null = null;
  private altBoxListenersAdded = false;
  private altBoxMouseMoveHandler: ((e: MouseEvent) => void) | null = null;
  private altBoxMouseUpHandler: ((e: MouseEvent) => void) | null = null;
  private altBoxLiveSelectedIds: Set<string> = new Set();
  private layoutChangedSignal = signal<LayoutType | null>(null);
  public linkLabelRenderer: HtmlEdgeLabelsRenderer | undefined;
  public gizmoOverlay: GizmoOverlay | undefined;

  // Signals for immediate UI updates of link label properties
  private linkLabelSignals = new Map<string, {
    position: ReturnType<typeof signal<number>>;
    offset: ReturnType<typeof signal<number>>;
  }>();

  constructor(private nodeService: NodeService,
              private modelService: ModelService,
              private mapStateService: MapStateService,
              private mapService: MapService,
              private sidebarService: SidebarService,
              private mapRoleService: MapRoleService,
              private commentStateManager: CommentsStateManager,
              private settingsService: SettingsService,
              private undoRedoManager: UndoRedoManagerService) {
    super();
    effect(() => {
      const isEditMode = this.sidebarService.getEditModeSignal();
      if (!this.cy) return;
      untracked(() => {
        if (isEditMode) {
          this.cy!.elements().addClass('edit-mode');
        } else {
          this.cy!.elements().removeClass('edit-mode');
          this.cy!.nodes().removeData('isNew');
        }

        // Clear class cache and redraw HTML edge labels when edit mode changes to update excludedFromState styling
        if (this.linkLabelRenderer) {
          this.linkLabelRenderer.clearClassCache();
          this.linkLabelRenderer.redraw();
        }
      });
    });

    effect(() => {
      const firstCommentAdded = this.commentStateManager.getFirstCommentAdded();
      if (firstCommentAdded) {
        if (!this.cy) return;
        const element = this.cy.getElementById(firstCommentAdded.elementId);
        if (element) {
          // Setting data triggers 'data' event which the label renderer handles
          element.data('hasComments', true);
          // Force redraw after data event handler completes
          if (element.isEdge() && this.linkLabelRenderer) {
            const renderer = this.linkLabelRenderer;
            setTimeout(() => renderer.redraw(), 10);
          }
        }
      }
    });

    effect(() => {
      const noComments = this.commentStateManager.getNoComments();
      if (noComments) {
        if (!this.cy) return;
        const element = this.cy.getElementById(noComments);
        if (element) {
          // Setting data triggers 'data' event which the label renderer handles
          element.data('hasComments', false);
          // Force redraw after data event handler completes
          if (element.isEdge() && this.linkLabelRenderer) {
            const renderer = this.linkLabelRenderer;
            setTimeout(() => renderer.redraw(), 10);
          }
        }
      }
    });

    // Effect for system dark mode changes only.
    // Signals MUST be read before the cy guard — otherwise the very first
    // run (during the constructor, when cy is still undefined) returns
    // before tracking systemDarkModeSignal, and the effect never re-runs
    // on subsequent OS appearance changes. MAP-1113.
    effect(() => {
      // Track system dark mode changes for appearance: 'system'
      this.settingsService.systemDarkModeSignal();
      const map = this.mapService.getSelectedMapSignal();
      if (!this.cy) return;
      // Only update if appearance is set to 'system'
      if (map?.styles?.appearance === 'system' || (map?.styles?.appearance == null && this.settingsService.getDefaultAppearance() === 'system')) {
        untracked(() => {
          this.updateStylesForAppearanceChange();
        });
      }
    });
  }

  createDefaultNode(pos?: { x: number, y: number }) {
    if (pos) {
      this.createNodeAtPosition('', DefaultValues.node.color, 'ELLIPSE', pos);
    } else {
      this.createNode('', DefaultValues.node.color, 'ELLIPSE', this.getCyBoundingBoxCenter());
    }
  }

  createNodeFromModel(model: any, position: { x: number, y: number }, parentNodeId?: string) {
    if (!this.cy) {
      return;
    }
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();

    // First instantiate the model to get the actual node data
    const instantiateDto = {
      context: {
        position: position,
        mapId: currentMap?.id || '',
        parentNodeId: parentNodeId
      }
    };

    // Use the model service to instantiate, which returns the complete node data
    this.modelService.instantiate(model.id, instantiateDto).subscribe({
      next: (res: any) => {
        if (res) {
          this.cy?.batch(() => {
            const newNode = this.cy?.add({
              group: 'nodes',
              data: {
                id: res.id,
                children: res.children,
                name: res.name,
                label: res.name,
                description: res.description, // Include description from model
                parent: res.parent,
                properties: res.properties,
                metadata: res.metadata, // Add metadata field
                type: res.type,
                // Don't set position in data - cytoscape will use the position from the backend
                color: res.color,
                weight: res.weight,
                imageUrl: res.imageUrl, // Ensure imageUrl is included for background-image styling
                isNew: true
              },
              position: { x: res.x as number, y: res.y as number } // Set position directly on element
            });

            // Push a single CreateNodeCommand capturing the fully model-populated
            // node (color/properties/metadata included) so undo removes exactly
            // what the model created in one step, instead of leaving the model's
            // setup untracked (MAP-1547).
            if (res.id && newNode) {
              const nodeSnapshot: GraphNodeSnapshot = {
                id: res.id,
                data: {...newNode.data(), id: res.id},
                position: {x: res.x as number, y: res.y as number},
                parentId: res.parent || parentNodeId || null,
                classes: newNode.classes()
              };
              const createNodeCommand = new CreateNodeCommand(
                this,
                this.nodeService,
                nodeSnapshot
              );
              this.undoRedoManager.pushCommand(createNodeCommand);
            }

            if (parentNodeId) {
              const parent = this.cy?.$(`#${parentNodeId}`);
              if (parent && parent.length > 0) {
                if (parent.data('collapsed')) {
                  const api = (this.cy as any).expandCollapse('get');
                  parent.removeClass('collapsed');
                  parent.addClass('expanded');
                  parent.data('collapsed', false);
                  api.expand(parent);
                }
                // Don't override position again - it's already set correctly
                newNode?.move({ parent: parent.id() });

                this.cy?.elements().unselect();

                if (isEmpty(parent.data('children'))) {
                  parent.data('children', [res]);
                }

                setTimeout(() => {
                  newNode?.select();
                  DashboardComponent.tappedNode = newNode?.data();
                  // Force style update to ensure images render properly
                  this.cy?.style().update();
                }, 100);
              }
            } else {
              // No parent - standalone node
              this.cy?.elements().unselect();
              setTimeout(() => {
                newNode?.select();
                DashboardComponent.tappedNode = newNode?.data();
                // Force style update to ensure images render properly
                this.cy?.style().update();
              }, 100);
            }
          });
        }
      },
      error: (error: any) => {
        console.error('Failed to create node from model:', error);
        // Fallback to creating a default node
        this.createNodeAtPosition('', DefaultValues.node.color, 'ELLIPSE', position);
      }
    });
  }

  getLayoutChangedSignal() {
    return this.layoutChangedSignal();
  }

  setLayoutChangedSignal(layout: LayoutType) {
    this.layoutChangedSignal.set(layout);
  }

  private getCyBoundingBoxCenter() {
    if (!this.cy) {
      return {
        x: 0,
        y: 0
      };
    }
    const bb = this.cy.elements().boundingBox();
    return {
      x: (bb.x1 + bb.x2) / 2,
      y: (bb.y1 + bb.y2) / 2
    };
  }

  createNode(name: string,
             color: string = DefaultValues.node.color,
             type: string, position: {
    x: number,
    y: number
  }, metadata?: any) {
    if (!this.cy) {
      return;
    }
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    // @ts-ignore
    const payload = {
      name,
      color,
      type,
      x: 0,
      y: 0,
      metadata,
      children: [],
      properties: DefaultNodeProperties,
      map: currentMap
    };
    this.nodeService.create(payload).subscribe((res) => {
      this.nodeService.loadSubject.next(res);
    });

  }

  createNodeAtPosition(name: string,
                       color: string = DefaultValues.node.color,
                       type: string,
                       position: { x: number; y: number },
                       metadata?: any) {
    if (!this.cy) {
      return;
    }
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    const payload = {
      name,
      color,
      type,
      x: position.x,
      y: position.y,
      metadata,
      properties: DefaultNodeProperties,
      children: [],
      map: currentMap
    };

    this.nodeService.create(payload).subscribe((res) => {
      this.nodeService.loadSubject.next(res);
    });
  }

  private readonly imageNaturalSizes = new Map<string, { width: number; height: number } | null>();

  private proxiedImageUrl(imageUrl: string): string {
    return environment.apiUrl + '/public/image?url=' + encodeURIComponent(imageUrl);
  }

  private imageNaturalSize(url: string): { width: number; height: number } | null {
    if (this.imageNaturalSizes.has(url)) return this.imageNaturalSizes.get(url) ?? null;
    this.imageNaturalSizes.set(url, null);
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      this.imageNaturalSizes.set(url, { width: img.naturalWidth, height: img.naturalHeight });
      this.cy?.style().update();
    };
    img.src = url;
    return null;
  }

  /** Background size of a scaled node image as percentages of the node box; null keeps the plain cover fit. */
  private nodeImageBackgroundSize(node: NodeSingular): { width: string; height: string } | null {
    const data = this.getNodeDataBasedOnState(node) as any;
    if (!data?.imageUrl) return null;
    const scale = resolveImageScale(data.properties?.imageScale);
    if (scale === 1) return null;
    const natural = this.imageNaturalSize(this.proxiedImageUrl(data.imageUrl));
    if (!natural) return null;
    let boxWidth = 1;
    let boxHeight = 1;
    if (node.isParent()) {
      const padding = Number((node as any).padding?.() ?? 0) * 2;
      boxWidth = node.width() + padding;
      boxHeight = node.height() + padding;
      if (!(boxWidth > 0) || !(boxHeight > 0)) return null;
    }
    const size = scaledCoverSize(boxWidth, boxHeight, natural.width, natural.height, scale);
    return {
      width: `${(size.width / boxWidth) * 100}%`,
      height: `${(size.height / boxHeight) * 100}%`,
    };
  }

  /** Shared with the dash pattern so the two cannot drift apart. */
  private nodeBorderWidth(node: NodeSingular | any): number {
    const data = this.getNodeDataBasedOnState(node) as any;
    const weight = data.weight;
    const hasBottleneck = data.metadata?.some((m: any) => m.key === 'bottleneck');
    if (weight !== undefined && weight !== null) {
      const w = weight * DefaultValues.weightMultiplier;
      if (hasBottleneck && w < 3) return 3;
      return w;
    }
    return hasBottleneck ? 3 : 0;
  }

  private linkLineWidth(link: EdgeSingular | any): number {
    const data = this.getLinkDataBasedOnState(link);
    const isGhostNode = Object.keys(data)?.length === 3;
    const weight = data.weight as number;
    if (weight && weight >= 1) return weight;
    if (weight && weight < 1) return weight === 0 ? 0.25 : 0.5;
    if (isGhostNode) return 1;
    return 0.1;
  }

  public resetAllSelected() {
    if (!this.cy) return;
    DashboardComponent.tappedNode = null;
    DashboardComponent.tappedLink = null;
    DashboardComponent.multiSelectedElements = [];
    if (!this.cy?.elements()) {
      return;
    }
    this.cy.elements().forEach((n) => {
      n.removeClass('highlighted');
      n.removeData('selected');
      n.removeClass('multi');
      n.unselect();
    });
  }

  public selectAllElements(): void {
    if (!this.cy) return;
    const cy = this.cy;
    const prevFocus = DashboardComponent.tappedNode || DashboardComponent.tappedLink;
    const allElements = cy.elements();
    DashboardComponent.multiSelectedElements = allElements.map((e) => e.data());
    allElements.select();

    const focusEle = (prevFocus?.id && cy.getElementById(prevFocus.id).nonempty())
      ? cy.getElementById(prevFocus.id)
      : (cy.nodes().length ? cy.nodes()[0] : cy.edges()[0]);
    this.applySelectAllFocus(focusEle);
  }

  public selectAllNodes(): void {
    if (!this.cy) return;
    const cy = this.cy;
    const prevTappedNode = DashboardComponent.tappedNode;
    // 'unselect' handlers re-select anything still listed in
    // multiSelectedElements, so it must be cleared before unselecting below.
    DashboardComponent.multiSelectedElements = [];
    cy.elements().unselect();
    const allNodes = cy.nodes();
    DashboardComponent.multiSelectedElements = allNodes.map((e) => e.data());
    allNodes.select();

    const focusEle = (prevTappedNode?.id && cy.getElementById(prevTappedNode.id).nonempty())
      ? cy.getElementById(prevTappedNode.id)
      : allNodes[0];
    this.applySelectAllFocus(focusEle);
  }

  public selectAllLinks(): void {
    if (!this.cy) return;
    const cy = this.cy;
    const prevTappedLink = DashboardComponent.tappedLink;
    // See selectAllNodes above.
    DashboardComponent.multiSelectedElements = [];
    cy.elements().unselect();
    const allEdges = cy.edges();
    DashboardComponent.multiSelectedElements = allEdges.map((e) => e.data());
    allEdges.select();

    const focusEle = (prevTappedLink?.id && cy.getElementById(prevTappedLink.id).nonempty())
      ? cy.getElementById(prevTappedLink.id)
      : allEdges[0];
    this.applySelectAllFocus(focusEle);
  }

  /**
   * Accepts a minimal structural shape rather than a specific cytoscape
   * collection type: @types/cytoscape models `CollectionReturnValue` and
   * `SingularElementReturnValue` as intersections of the node- and
   * edge-specific interfaces, so neither a bare `NodeSingular` nor a bare
   * `EdgeSingular` (what `cy.nodes()[0]`/`cy.edges()[0]` actually return) is
   * assignable to them. This just needs the three methods used below.
   */
  private applySelectAllFocus(ele: { empty(): boolean; data(): any; addClass(cls: string): unknown } | undefined): void {
    if (!ele || ele.empty()) {
      DashboardComponent.tappedNode = null;
      DashboardComponent.tappedLink = null;
      return;
    }
    const data = ele.data();
    if (this.isElementNode(data)) {
      // Spread to a new object reference so Angular change detection picks up the focus change.
      DashboardComponent.tappedNode = {...data};
      DashboardComponent.tappedLink = null;
      ele.addClass('multi');
    } else {
      DashboardComponent.tappedLink = {...data};
      DashboardComponent.tappedNode = null;
      ele.addClass('highlighted');
    }
  }

  // Helper methods for link label signals
  private getOrCreateLinkLabelSignals(linkId: string) {
    if (!this.linkLabelSignals.has(linkId)) {
      // Get actual edge data to initialize signals with correct values
      const edge = this.cy?.getElementById(linkId);
      const edgeData = edge?.data();
      const initialPosition = edgeData?.linkLabelPosition ?? 0;
      const initialOffset = edgeData?.linkLabelOffset ?? DefaultValues.link.linkLabelOffset;

      this.linkLabelSignals.set(linkId, {
        position: signal(initialPosition),
        offset: signal(initialOffset)
      });
    }
    return this.linkLabelSignals.get(linkId)!;
  }

  public updateLinkLabelPosition(linkId: string, position: number) {
    const signals = this.getOrCreateLinkLabelSignals(linkId);
    signals.position.set(position);

    // Also update the Cytoscape edge data immediately
    if (this.cy) {
      const edge = this.cy.getElementById(linkId);
      if (edge && edge.length > 0) {
        edge.data('linkLabelPosition', position);
      }
    }

    // Trigger redraw if link label renderer exists
    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }
  }

  public updateLinkLabelOffset(linkId: string, offset: number) {
    const signals = this.getOrCreateLinkLabelSignals(linkId);
    signals.offset.set(offset);

    // Also update the Cytoscape edge data immediately
    if (this.cy) {
      const edge = this.cy.getElementById(linkId);
      if (edge && edge.length > 0) {
        edge.data('linkLabelOffset', offset);
      }
    }

    // Trigger redraw if link label renderer exists
    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }
  }

  // Visual-only updates (don't update edge data, only signals for rendering)
  public updateLinkLabelPositionVisualOnly(linkId: string, position: number) {
    const signals = this.getOrCreateLinkLabelSignals(linkId);
    signals.position.set(position);

    // Trigger redraw if link label renderer exists
    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }
  }

  public updateLinkLabelOffsetVisualOnly(linkId: string, offset: number) {
    const signals = this.getOrCreateLinkLabelSignals(linkId);
    signals.offset.set(offset);

    // Trigger redraw if link label renderer exists
    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }
  }


  private getLinkLabelPosition(linkId: string, fallback: number): number {
    const signals = this.linkLabelSignals.get(linkId);
    return signals ? signals.position() : fallback;
  }

  private getLinkLabelOffset(linkId: string, fallback: number): number {
    const signals = this.linkLabelSignals.get(linkId);
    return signals ? signals.offset() : fallback;
  }

  /**
   * MAP-1559: Temporary/approximate fix for link labels landing inside a much
   * bigger node's shape when the source and target nodes have very different
   * sizes (typically containers).
   *
   * The stored `linkLabelPosition` (alignment) ranges [-1, 1]: -1 = source
   * center, 1 = target center, 0 = midpoint. This clamps that range, for
   * RENDERING ONLY, to [AS, AT] - the alignment values that would place the
   * label right on the source's / target's border - assuming a straight line
   * between the two node centers (the approximation). The raw stored value
   * (DB / form slider / undo-redo) is never touched; only the position handed
   * to the label renderer is remapped.
   *
   * AS = 2 * (sourceRadius / distance) - 1
   * AT = 1 - 2 * (targetRadius / distance)
   *
   * Example: AS = -0.8, AT = 0.2 -> raw -1 renders as -0.8, raw 1 renders as
   * 0.2, raw 0 renders as (-0.8 + 0.2) / 2 = -0.3, everything else interpolates
   * linearly between.
   */
  private clampLinkLabelAlignmentForRendering(rawAlignment: number, edge: EdgeSingular): number {
    if (!edge || typeof edge.source !== 'function') return rawAlignment;

    const source = edge.source();
    const target = edge.target();
    if (!source || !target || source.empty() || target.empty()) return rawAlignment;

    const sourcePos = source.position();
    const targetPos = target.position();
    const distance = Math.hypot(targetPos.x - sourcePos.x, targetPos.y - sourcePos.y);
    if (!distance) return rawAlignment;

    const sourceRadius = this.getNodeBoundingRadius(source);
    const targetRadius = this.getNodeBoundingRadius(target);

    const alignmentAtSourceBorder = Math.max(-1, Math.min(1, 2 * (sourceRadius / distance) - 1));
    const alignmentAtTargetBorder = Math.max(-1, Math.min(1, 1 - 2 * (targetRadius / distance)));

    // Nodes overlap or are too close for a meaningful [AS, AT] window (e.g. two
    // huge nodes right next to each other) - fall back to the raw value rather
    // than clamping to an inverted/degenerate range.
    if (alignmentAtSourceBorder >= alignmentAtTargetBorder) return rawAlignment;

    const clampedRaw = Math.max(-1, Math.min(1, rawAlignment));
    const normalized = (clampedRaw + 1) / 2; // raw [-1, 1] -> [0, 1]
    return alignmentAtSourceBorder + normalized * (alignmentAtTargetBorder - alignmentAtSourceBorder);
  }

  /**
   * Approximate bounding "radius" of a node, reusing the same
   * `boundingBox({ includeLabels: false })` measure the codebase already uses
   * for node geometry elsewhere (see `CyMaporiumServiceCore.getDistance`).
   * Works for regular nodes as well as auto-sized compound/container nodes,
   * since Cytoscape keeps a compound node's bounding box fitted to its
   * children. Averages the half-width and half-height as a single scalar
   * radius (a circle approximation) since the clamp itself already assumes a
   * straight line between node centers.
   */
  private getNodeBoundingRadius(node: NodeSingular): number {
    const bb = node.boundingBox({ includeLabels: false });
    return ((bb.x2 - bb.x1) + (bb.y2 - bb.y1)) / 4;
  }

  private updateHtmlEdgeLabelColors(): void {
    const map = this.mapService.getSelectedMapSignal();
    const isDark = this.settingsService.isDarkMode(map?.styles?.appearance);
    const color = isDark ? '#FFFFFF' : '#000000';

    // Update CSS custom property for HTML edge labels
    document.documentElement.style.setProperty('--html-edge-label-color', color);
  }

  public clearLinkLabelSignals(): void {
    this.linkLabelSignals.clear();
  }

  public centerCanvas(zoom = true,
                      preventPanZoomStoring = false,
                      forceCenter = false,
                      animate = true,
                      isHomeButton = false) {
    if (!this.cy || isRestoringPanZoom() && !forceCenter) {
      return;
    }

    centeringInProgress.set(preventPanZoomStoring);

    // Only invalidate pan/zoom state for explicit user actions (home button)
    // Don't invalidate during automatic centering or restoration
    if (isHomeButton || forceCenter) {
      invalidatePanZoomForCollapsedNode();
      invalidateGeneralPanZoom();
    }

    // Set home button centering flag to prevent saving pan/zoom state
    if (isHomeButton) {
      setHomeButtonCentering(true);
    }

    this.centerOnElements(this.cy, this.cy.elements(), zoom, 1, 16, animate);
  }


  /**
   * Unified handler for deleting a link and updating sibling link curvatures
   */
  public deleteLink(ele: any, contextParams?: any): void {
    const edgeData = ele.data();
    const edgeId = edgeData.id;

    this.nodeService.deleteLink(edgeId).subscribe(() => {
      ele.unselect();
      ele.remove(); // Actually remove from Cytoscape graph so parallel edges recalculate

      // Update UI state if context params provided (from context menu)
      if (contextParams) {
        this.cy?.resize();
        contextParams.selectedLinks = contextParams.selectedLinks.filter((link: any) => link.id !== edgeData.id);
        if (contextParams.selectedLinks.length === 1 && contextParams.selectedLinks[0].id === edgeData.id) {
          contextParams.selectedLinks = [];
        }
        if (DashboardComponent.tappedLink?.id === edgeData.id) {
          DashboardComponent.tappedLink = null;
        }
        updateInProgress.set(false);
      } else {
        // From keyboard handler - just update UI
        this.cy?.resize();
        if (DashboardComponent.tappedLink?.id === edgeData.id) {
          DashboardComponent.tappedLink = null;
        }
      }
    });
  }

  public centerOnElements(cy: Core,
                          elements: any,
                          zoom = false,
                          percentage = 1,
                          margin = 16,
                          animate = true) {
    const sidebarElement = document.getElementsByClassName('drawer')[0] as HTMLElement;
    const toolbarElement = document.getElementsByClassName('navbar')[0] as HTMLElement;

    // Check if sidebar is visually open by looking for mat-drawer-opened class on container
    const drawerContainer = document.querySelector('mat-drawer-container');
    const isSidebarOpen = drawerContainer?.classList.contains('mat-drawer-container-has-open');

    // Get sidebar width from element or CSS variable fallback
    let sidebarWidth = sidebarElement?.offsetWidth || 0;
    if (isSidebarOpen && sidebarWidth === 0) {
      // Fallback to CSS variable if offsetWidth returns 0
      const cssWidth = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width');
      sidebarWidth = parseInt(cssWidth) || 0;
    }

    const toolbarHeight = toolbarElement?.offsetHeight || 0;
    const isSidebarToggled = isSidebarOpen && sidebarWidth > 0;

    const viewportSize = {
      width: isSidebarToggled ? cy.width() - sidebarWidth : cy.width(),
      height: cy.height() - toolbarHeight
    };

    const panZoomTransition = (pan: any,
                               zoom: any | null,
                               shouldAnimate = true) => {
      const params = {
        pan
      };
      if (zoom) {
        set(params, 'zoom', zoom);
      }
      if (shouldAnimate) {
        cy.animate({
          ...params,
          duration: 500,
          easing: 'ease',
          complete() {
              ExpandCollapseInProgress.set(false);
            centeringInProgress.set(false);
            // Save pan/zoom state immediately after animation completes
            savePanZoomState();
            // Mark restoration as completed so future pan/zoom events can be saved
            setRestorationCompleted(true);
            // Reset home button centering flag
            setHomeButtonCentering(false);
          }
        });
      } else {
        cy?.pan(params.pan);
        //@ts-ignore
        if (params.zoom) {
          //@ts-ignore
          cy?.zoom(params.zoom);
        }
        ExpandCollapseInProgress.set(false);
        centeringInProgress.set(false);
        // Save pan/zoom state immediately after pan/zoom change
        savePanZoomState();
        // Mark restoration as completed so future pan/zoom events can be saved
        setRestorationCompleted(true);
        // Reset home button centering flag
        setHomeButtonCentering(false);
      }
    };

    // Filter out hidden elements
    const filteredElements = elements.filter((ele: any) => !ele.hidden());

    // Calculate the bounding box for the collection of filtered elements and add the margin
    const boundingBox = filteredElements.boundingBox();
    const boundingBoxWithMargin = {
      x1: boundingBox.x1 - margin,
      y1: boundingBox.y1 - margin,
      x2: boundingBox.x2 + margin,
      y2: boundingBox.y2 + margin
    };

    // Find the center of the bounding box with margin
    const targetPosition = {
      x: (boundingBoxWithMargin.x1 + boundingBoxWithMargin.x2) / 2,
      y: (boundingBoxWithMargin.y1 + boundingBoxWithMargin.y2) / 2
    };

    const currentPan = cy.pan();
    const currentZoom = cy.zoom();

    // Convert the target position to account for the current zoom level
    const zoomedTargetPosition = {
      x: targetPosition.x * currentZoom,
      y: targetPosition.y * currentZoom
    };

    // Calculate the new pan position to center the elements
    const newPan = {
      x: currentPan.x - (zoomedTargetPosition.x + currentPan.x - viewportSize.width / 2),
      y: currentPan.y - (zoomedTargetPosition.y + currentPan.y - viewportSize.height / 2)
    };

    if (zoom) {
      // Calculate the zoom level to fit the bounding box within the viewport
      const fitZoomLevel = Math.min(
        viewportSize.width / (boundingBoxWithMargin.x2 - boundingBoxWithMargin.x1),
        viewportSize.height / (boundingBoxWithMargin.y2 - boundingBoxWithMargin.y1)
      );

      // Adjust the zoom level based on the percentage
      const zoomLevel = fitZoomLevel * percentage;

      // Calculate the new pan position with the new zoom level
      const newZoomedTargetPosition = {
        x: targetPosition.x * zoomLevel,
        y: targetPosition.y * zoomLevel
      };

      const newZoomedPan = {
        x: (viewportSize.width / 2) - newZoomedTargetPosition.x,
        y: (viewportSize.height / 2) - newZoomedTargetPosition.y + toolbarHeight / 2
      };

      // Set flags BEFORE starting animation to prevent interference
      ExpandCollapseInProgress.set(true);
      centeringInProgress.set(true);
      panZoomTransition(newZoomedPan, zoomLevel, animate);
    } else {
      // Set flags BEFORE starting animation to prevent interference
      ExpandCollapseInProgress.set(true);
      centeringInProgress.set(true);
      // Animate the graph to the new pan position
      panZoomTransition(newPan, null, animate);
    }
  }

  //@ts-ignore
  getNodePagePosition(node: NodeSingular, options: { xOffset: number, yOffset: number, centerOnNode: boolean } = {
    xOffset: 0,
    yOffset: 0,
    centerOnNode: true
  }) {
    if (!this.cy) return null;

    const cy = this.cy;
    const pan = cy.pan();
    const zoom = cy.zoom();

    // Determine accurate node position
    let pos;
    if ((node.data()?.children?.length ?? 0) > 0) {
      // Compound node: use bounding box center
      const bb = node.boundingBox({ includeLabels: true, includeOverlays: true });
      pos = {
        x: (bb.x1 + bb.x2) / 2,
        y: (bb.y1 + bb.y2) / 2
      };
    } else {
      // Regular node
      pos = node.position();
    }

    const renderedX = pos.x * zoom + pan.x;
    const renderedY = pos.y * zoom + pan.y;

    // @ts-ignore
    const rect = cy.container().getBoundingClientRect();
    const pageXOffset = window.pageXOffset || document.documentElement.scrollLeft;
    const pageYOffset = window.pageYOffset || document.documentElement.scrollTop;

    let left = rect.left + pageXOffset + renderedX;
    let top = rect.top + pageYOffset + renderedY;

    if (options.centerOnNode) {
      const nodeWidth = node.renderedWidth();
      const nodeHeight = node.renderedHeight();
      left -= nodeWidth / 2;
      top -= nodeHeight / 2;
    }

    left += options.xOffset;
    top += options.yOffset;

    return { left: left - 15, top };
  }


  /**
   * Finds the center-most node relative to the visible canvas.
   * @returns {cytoscape.NodeSingular | null} The center-most node or null if no visible nodes.
   */
  getCenterMostNode(sidebarWidth: number): cytoscape.NodeSingular | null {
    if (!this.cy) {
      console.error('Cytoscape instance is not initialized.');
      return null;
    }

    const pan = this.cy.pan();
    const zoom = this.cy.zoom();

    // Calculate the horizontal center of the visible portion of the canvas.
    const pixelCenterX = (this.cy.width() - sidebarWidth) / 2;
    const pixelCenterY = this.cy.height() / 2;

    // Convert to model coordinates
    const viewportCenter = {
      x: (pixelCenterX - pan.x) / zoom,
      y: (pixelCenterY - pan.y) / zoom
    };

    const visibleNodes = this.cy.nodes(':visible');
    if (visibleNodes.length === 0) {
      console.warn('No visible nodes found.');
      return null;
    }

    // A node is compound if it has children
    const isCompoundNode = (node: cytoscape.NodeSingular) => (node.data()?.children?.length ?? 0) > 0;

    // Separate compound and leaf nodes
    const compoundNodes = visibleNodes.filter(isCompoundNode);

    // Leaf nodes that are not children of compound nodes
    // If a leaf node has a parent that is compound, exclude it.
    const leafNodes = visibleNodes.filter((node) => {
      const isLeaf = (node.data()?.children?.length ?? 0) === 0;
      if (!isLeaf) return false;

      const parent = node.parent();
      //@ts-ignore
      if (parent && isCompoundNode(parent)) {
        // This leaf node belongs to a compound node, so exclude it
        return false;
      }

      return true;
    });

    // Helper to find the closest node and its distance
    const findClosestNodeWithDistance = (nodes: cytoscape.CollectionReturnValue): {
      node: cytoscape.NodeSingular | null;
      distance: number
    } => {
      let closestNode: cytoscape.NodeSingular | null = null;
      let minDistance = Infinity;

      nodes.forEach((node) => {
        let position;
        if (isCompoundNode(node)) {
          const bb = node.boundingBox({ includeLabels: true, includeOverlays: true });
          position = {
            x: (bb.x1 + bb.x2) / 2,
            y: (bb.y1 + bb.y2) / 2
          };
        } else {
          position = node.position();
        }
        const dx = position.x - viewportCenter.x;
        const dy = position.y - viewportCenter.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance) {
          minDistance = distance;
          closestNode = node;
        }
      });

      return { node: closestNode, distance: minDistance };
    };

    const compoundResult = compoundNodes.length > 0 ? findClosestNodeWithDistance(compoundNodes) : {
      node: null,
      distance: Infinity
    };
    const leafResult = leafNodes.length > 0 ? findClosestNodeWithDistance(leafNodes) : {
      node: null,
      distance: Infinity
    };

    // Compare distances and return whichever is closer
    // If a compound node is closer, it automatically takes precedence.
    // Leaf nodes that belong to a compound node are no longer considered.
    if (compoundResult.distance < leafResult.distance) {
      return compoundResult.node;
    } else {
      if (leafResult.node) {
      }
      return leafResult.node;
    }
  }

  public expandMultipleNodes(nodes: cytoscape.NodeSingular[],
                             update = false,
                             savePan = true) {
    //@ts-ignore
    const api = this.cy.expandCollapse('get');

    const handler = (node: NodeSingular) => {

      api.expand(node);
      node.addClass('expanded');
      node.removeClass('collapsed');
      node.data('collapsed', false);
      // @ts-ignore
      const child = node.children();
      // @ts-ignore
      child.forEach((c: any) => {

        if (c.data('collapsed')) {
          c.data('collapsed', true);
          c.addClass('collapsed');
          c.removeClass('expanded');
          api.collapse(c);
        }
      });
    };
    nodes.forEach((node: any) => {
      handler(node);
    });

    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }

    if (this.cy) {
      const node = first(nodes);

      if (node) {
        if (savePan) {
          setPanZoomForCollapsedNode(node.data('id'), true);
        }
      }
    }
    if (update) {
      return from(nodes).pipe(
        mergeMap((node: any) => {
          // Node is already expanded above (lines 773-775), just return data for network update
          return of(node.data());
        })
      );
    } else {
      return of(null);
    }

  }

  // In CytoscapeService
  public buildEdgeLabel(edge: EdgeSingular): string {
    // Use your state-aware link data
    const data = this.getLinkDataBasedOnState(edge) as any;
    if (!data) return '';

    // Base label source: 'name' or metadata key
    let name = '';
    if (data.label === 'name' || !data.label) {
      // Default to using 'name' if label field is undefined or explicitly set to 'name'
      name = data.name || '';
    } else if (Array.isArray(data.metadata) && data.metadata.length > 0 && data.label) {
      const hit = data.metadata.find((m: { key: string; value: string }) => m.key === data.label);
      name = hit?.value || '';
    }

    return name;
  }

  public getEdgeLabelClasses(edge: EdgeSingular): string {
    const data = this.getLinkDataBasedOnState(edge) as any;
    if (!data) return '';

    const classes = ['html-edge-label'];

    // Add class for link icon if any metadata value is a URL
    const hasUrl = Array.isArray(data.metadata) && data.metadata.some((m: any) => this.containsUrl(m?.value));
    if (hasUrl) {
      classes.push('has-links');
    }

    // Add class for comments icon - read directly from edge data to get real-time value set by effect
    if (edge.data('hasComments') && this.canSeeComments()) {
      classes.push('has-comments');
    }

    // Add class for excluded from state
    if (data.excludedFromState) {
      const isEditMode = this.sidebarService.getEditModeSignal();
      const hasEditPermission = this.hasMapPermission('edit') || this.hasMapPermission('manage');

      if (!isEditMode || isInIframe() || (!hasEditPermission)) {
        // In read mode: hide completely
        classes.push('excluded-from-state-hidden');
      } else {
        // In edit mode: make transparent
        classes.push('excluded-from-state-transparent');
      }
    }

    return classes.join(' ');
  }


  public expandNode(event: any, update = false) {
    //@ts-ignore
    const api = this.cy.expandCollapse('get');
    const node = event.target.data();
    const handler = () => {
      api.expand(event.target);
      event.target.addClass('expanded');
      event.target.removeClass('collapsed');
      // @ts-ignore
      const child = event.target.children();
      // @ts-ignore
      child.forEach((c: any) => {
        if (c.data('collapsed')) {
          c.data('collapsed', true);
          c.addClass('collapsed');
          c.removeClass('expanded');
          api.collapse(c);
        }
      });
    };

    node.collapsed = false;
    handler();

    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }

    if (this.cy) {
      setPanZoomForCollapsedNode(event.target.data('id'), true);
    }
    if (update) {
      return of(node);
    } else {
      return of(null);
    }

  }

  collapseMultipleNodes(nodes: cytoscape.NodeSingular[],
                        update = false, restore = true) {
    //@ts-ignore
    const api = this.cy.expandCollapse('get');
    const handler = (node: NodeSingular) => {
      if (!node.isParent()) {
        return;
      }
      api.collapse(node);
      node.addClass('collapsed');
      node.removeClass('expanded');
      node.data('collapsed', true);
    };
    nodes.forEach((node: any) => {
      handler(node);
    });

    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }

    if (restore) {
      this.restorePanZoomForCollapsedNode(first(nodes)?.data('id'));
    }
    if (update) {
      return from(nodes).pipe(
        mergeMap((node: any) => {
          // Node is already collapsed above (lines 900-902), just return data for network update
          return of(node.data());
        })
      );
    } else {
      return of(null);
    }
  }

  public collapseNode(event: any, update = false) {
    //@ts-ignore
    const api = this.cy.expandCollapse('get');
    const node = event.target.data();
    node.collapsed = true;
    const handler = () => {
      api.collapse(event.target);
      event.target.addClass('collapsed');
      event.target.removeClass('expanded');
    };
    handler();

    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.redraw();
    }

    if (update) {
      return of(node);
    } else {
      return of(null);
    }
  }

  public restorePanZoomForCollapsedNode(nodeId: string) {
    if (!nodeId) return;
    if (this.cy) {
      restoreSavedPanZoomForCollapsedNode(nodeId, this.cy);
    }
  }

  public initAnchors(edges: EdgeSingular[],
                     type: string) {
    if (!this.cy) return;
    const cy = this.cy;
    // @ts-ignore
    if (!cy.edgeEditing) return;
    // @ts-ignore
    const edgeEditingInstance = cy.edgeEditing('get');

    const edgeEditingType = type === 'BEZIER' ? 'none' : type === 'STRAIGHT' ? 'bend' : 'control';
    edgeEditingInstance.initAnchorPoints(edges, edgeEditingType);
  }

  protected addEventListeners(): void {
    if (!this.cy) return;

    // this.cy.on('drag', 'node', (event) => this.onNodeDrag(event));
    this.cy.on('free', 'node', this.onNodeDragFree.bind(this));
    this.cy.on('grab', 'node', this.onGrabNode.bind(this));

    // Reinitialize anchor points when edges are selected (only if missing)
    this.cy.on('select', 'edge', (event) => {
      const edge = event.target;
      const edgeType = edge.data('type');
      if (edgeType !== 'BEZIER') {
        const controlPoints = edge.data('controlPointPositions');
        const bendPoints = edge.data('bendPointPositions');

        // Only initialize if control/bend points are missing or empty
        if ((!controlPoints || controlPoints.length === 0) && (!bendPoints || bendPoints.length === 0)) {
          const edgeEditingInstance = (this.cy as any).edgeEditing('get');
          edgeEditingInstance.initAnchorPoints([edge])
        }
      }
    });

    // Track edge anchor point changes for undo/redo
    // Prevent duplicate registration by removing previous handler
    const existingHandler = (this as any)._edgeMoveEndHandler;
    if (existingHandler) {
      this.cy.off('edgeediting_moveend', existingHandler);
    }

    // Use a shared pending state to debounce rapid-fire events
    let pendingEdgeAnchorCommand: {
      edgeId: string;
      beforeState: EdgeControlPointSnapshot;
      timeoutId: ReturnType<typeof setTimeout>;
    } | null = null;

    const handler = (event: any, edge: any, beforeState: any) => {
      // Skip if undo/redo is in progress to prevent duplicate commands
      if (this.undoRedoManager.isOperating()) {
        return;
      }

      if (!edge || !beforeState) {
        return;
      }

      const edgeId = edge.id();

      // If we have a pending command for the same edge, clear it (we'll create a new one with updated after state)
      if (pendingEdgeAnchorCommand && pendingEdgeAnchorCommand.edgeId === edgeId) {
        clearTimeout(pendingEdgeAnchorCommand.timeoutId);
        // Keep the ORIGINAL beforeState from the first event
        beforeState = pendingEdgeAnchorCommand.beforeState;
      }

      // Store pending command and process after a short delay to catch duplicate events
      const timeoutId = setTimeout(() => {
        pendingEdgeAnchorCommand = null;

        // Capture the current (after) state
        const afterState = this.captureEdgeControlPointSnapshot(edge);
        if (!afterState) {
          return;
        }

        // Helper to normalize null/undefined/empty arrays for comparison
        const normalize = (val: any) => {
          if (val === null || val === undefined) return null;
          if (Array.isArray(val) && val.length === 0) return null;
          return JSON.stringify(val);
        };

        // Only create command if something actually changed
        const hasChanged =
            normalize(beforeState.bendPointPositions) !== normalize(afterState.bendPointPositions) ||
            normalize(beforeState.controlPointPositions) !== normalize(afterState.controlPointPositions) ||
            normalize(beforeState.cyedgebendeditingWeights) !== normalize(afterState.cyedgebendeditingWeights) ||
            normalize(beforeState.cyedgebendeditingDistances) !== normalize(afterState.cyedgebendeditingDistances) ||
            normalize(beforeState.cyedgecontroleditingWeights) !== normalize(afterState.cyedgecontroleditingWeights) ||
            normalize(beforeState.cyedgecontroleditingDistances) !== normalize(afterState.cyedgecontroleditingDistances);

        if (hasChanged) {
          const command = new EdgeAnchorCommand(this, this.nodeService, beforeState, afterState);
          this.undoRedoManager.pushCommand(command);
        }
      }, 50); // 50ms debounce

      pendingEdgeAnchorCommand = {edgeId, beforeState, timeoutId};
    };

    (this as any)._edgeMoveEndHandler = handler;
    this.cy.on('edgeediting_moveend', handler);

    // TODO: No zoom changes MAP-614 - Display all nodes at all times
    // this.cy.on('zoom', () => this.debouncedZoomLevelChange());

    this.cy.on('drag', 'node', (event) => {
      if (!this.cy) return;

      const grabbedPos = event.target.data('grabbedPosition');
      const currentPos = event.target.position();
      const draggedDistance = Math.abs(currentPos.x - grabbedPos.x) + Math.abs(currentPos.y - grabbedPos.y);
      this.draggedDistance = draggedDistance;

      // MAP-1270: Move follower nodes (previously selected nodes during shift-drag)
      if (CytoscapeService.shiftDragFollowers.size > 0) {
        const deltaX = currentPos.x - grabbedPos.x;
        const deltaY = currentPos.y - grabbedPos.y;
        CytoscapeService.shiftDragFollowers.forEach((initialPos, nodeId) => {
          const followerNode = this.cy!.$id(nodeId);
          if (followerNode.length > 0) {
            followerNode.position({
              x: initialPos.x + deltaX,
              y: initialPos.y + deltaY
            });
          }
        });
        // Update link labels to follow the moved nodes
        if (this.linkLabelRenderer) {
          this.linkLabelRenderer.updateLabelPositions();
        }
      }

      // Only run expensive setup operations once when drag starts
      if (draggedDistance > 1 && !this.dragStarted) {
        this.dragStarted = true;

        const map = this.mapService.getSelectedMapSignal();
        const isDark = this.settingsService.isDarkMode(map?.styles?.appearance);
        event.target.style = {
          'overlay-opacity': SettingsService.sharedValues.node.nodeDragOpacity,
          'overlay-padding': '2px',
          'overlay-color': isDark ? '#FFFFFF' : SettingsService.sharedValues.node.nodeHighlightColor,
          'opacity': () => 0
        };
        const isMultiSelection = this.cy.$(':selected').length > 1;

        if (!isMultiSelection) {
          const selectedElements = this.cy.$(':selected');
          DashboardComponent.multiSelectedElements = [];
          selectedElements.unselect();

          if (event.target.data('draggedByParent')) {
            return;
          }

          event.target.addClass('grabbed');
          const currentState = this.mapStateService.getCurrentLocalState();
          let elementData = event.target.data();
          if (currentState?.id !== undefined) {
            const statedElement = elementData.states?.find((s: any) => s.stateId === currentState.id);
            if (statedElement?.link || statedElement?.node) {
              if (event.target.isNode()) {
                elementData = statedElement?.node;
              } else {
                elementData = statedElement?.link;
              }
            }
          }

          if (event.target.isNode()) {
            DashboardComponent.tappedNode = elementData;
            DashboardComponent.tappedLink = null;
          } else {
            DashboardComponent.tappedLink = elementData;
            DashboardComponent.tappedNode = null;
          }

          event.target.select();
        } else {
          if (!event.target.selected()) {
            this.cy.$(':selected').forEach((node) => {
              node.removeClass('grabbed');
              node.addClass('force-unselect');
              node.unselect();
            });
            DashboardComponent.multiSelectedElements = [];
            event.target.select();
            event.target.addClass('grabbed');
            if (event.target.isNode()) {
              DashboardComponent.tappedNode = event.target.data();
              DashboardComponent.tappedLink = null;
            } else {
              DashboardComponent.tappedLink = event.target.data();
              DashboardComponent.tappedNode = null;
            }
          }
        }
      }

      if (!this.mapStateService.isDefaultState()) {
        return;
      }

      this.updateDragHoverState(event);

    });

    this.cy.on('free', (event) => {
      // @ts-ignore
      this.cy.nodes('.highlighted').removeClass('highlighted');
      this.dragContext = null;
      this.dragStarted = false;
      // MAP-1270: Clear shift-drag followers
      CytoscapeService.shiftDragFollowers.clear();
    });

    // Invalidate stored pan-zoom state when the user pans or zooms the graph
    this.cy.on('pan zoom', (event) => {
      const expandCollapseInProgress = ExpandCollapseInProgress();
      if (expandCollapseInProgress) return;
      // only invalidate if moved beyond the threshold
      invalidatePanZoomForCollapsedNode();
    });
    this.handleBoxSelectionEvents(this.cy);
    this.handleForcePan();
    this.handleAltBoxSelection();
  }

  private dragContext: {
    candidates: cytoscape.CollectionReturnValue;
    closestNodeId?: string;
  } | null = null;

  private updateDragHoverState(event: cytoscape.EventObject) {
    if (!this.cy) return;
    const draggedNode = event.target;
    if (!draggedNode?.isNode || !draggedNode.isNode()) {
      return;
    }
    if (draggedNode.data('draggedByParent')) return;

    if (!this.dragContext) {
      this.prepareDragContext(draggedNode);
    }

    const context = this.dragContext;
    if (!context) return;

    const candidates = context.candidates;

    const renderedPoint = this.resolveRenderedDragPoint(event);
    if (!renderedPoint) return;

    const dropCandidates: DropCandidate[] = [];
    candidates.forEach((candidate: cytoscape.SingularElementReturnValue) => {
      const nodeCandidate = candidate as NodeSingular;
      if (!nodeCandidate || nodeCandidate.removed()) return;
      if (nodeCandidate.id() === draggedNode.id()) return;
      if (nodeCandidate.selected()) return;
      if (nodeCandidate.hidden() || nodeCandidate.hasClass('hidden')) return;

      // boundingBox() defaults to includeLabels: true, which would let a long
      // label catch the drag long before the node shape itself is near it.
      dropCandidates.push({
        id: nodeCandidate.id(),
        box: nodeCandidate.renderedBoundingBox({ includeLabels: false }),
        isParent: nodeCandidate.isParent(),
        depth: nodeCandidate.ancestors().length
      });
    });

    const previousId = context.closestNodeId;
    const newId = pickDropTarget(renderedPoint, dropCandidates);

    if (previousId && previousId !== newId) {
      const prevNode = this.cy.getElementById(previousId);
      if (prevNode && prevNode.length) {
        prevNode.removeClass('highlighted');
      }
    }

    if (newId) {
      this.cy.getElementById(newId).addClass('highlighted');
    }

    context.closestNodeId = newId;
  }

  /**
   * cytoscape-grid-guide's snap-to-grid runs a "discrete drag": it locks the node
   * on tapstart and re-emits `drag` with `attachedNode.trigger('drag')`, an event
   * that carries no position at all. Snap-to-guides locks the node while it sits on
   * an alignment line, which suppresses cytoscape's own positioned `drag` events.
   * Falling back to the tracked cursor keeps drop-target detection alive in both.
   */
  private resolveRenderedDragPoint(event: cytoscape.EventObject): { x: number; y: number } | null {
    if (!this.cy) return null;
    if (event.renderedPosition) return event.renderedPosition;

    const zoom = this.cy.zoom();
    const pan = this.cy.pan();
    const modelPoint = event.position || this.getMousePosition();
    if (!modelPoint) return null;

    return {
      x: modelPoint.x * zoom + pan.x,
      y: modelPoint.y * zoom + pan.y
    };
  }

  private prepareDragContext(draggedNode: NodeSingular) {
    if (!this.cy) {
      this.dragContext = null;
      return;
    }

    let candidates: cytoscape.CollectionReturnValue;
    const descendants = draggedNode.descendants();
    const ancestors = draggedNode.ancestors();

    if (draggedNode.isChild()) {
      const parent = draggedNode.parent();
      candidates = parent && parent.length
        ? parent.descendants().difference(draggedNode).difference(descendants)
        : this.cy.collection();
    } else {
      const excluded = descendants.union(draggedNode).union(ancestors);
      candidates = this.cy.nodes().difference(excluded);
    }

    this.dragContext = {
      candidates,
      closestNodeId: undefined
    };
  }

  private boxEndPosition: { x: number; y: number } | null = null;

  private handleBoxSelectionEvents(cy: Core) {
    cy.on('box', (event) => {
      this.boxSelectionElements.push(event.target as any);
    });

    cy.on('boxend', async (event) => {
      this.boxEndPosition = event.position || event.renderedPosition;
      await new Promise(resolve => setTimeout(resolve, 100));

      if (this.boxSelectionElements.length === 0) {
        return;
      }

      this.handleDragSelectionEnd(cy, (event.originalEvent as MouseEvent)?.shiftKey);

      const pos = event.renderedPosition || this.boxEndPosition;

      const isPointInsideBox = (point: { x: number; y: number }, box: any): boolean => {
        return (
          point.x >= box.x1 &&
          point.x <= box.x2 &&
          point.y >= box.y1 &&
          point.y <= box.y2
        );
      };

      const node = cy.nodes().filter(n => {
        const box = n.renderedBoundingBox();
        return isPointInsideBox(pos, box);
      }).first();

      const edge = node.empty()
        ? cy.edges().filter(e => {
          const box = e.renderedBoundingBox();
          return isPointInsideBox(pos, box);
        }).first()
        : null;

      if (node.nonempty()) {
        DashboardComponent.tappedNode = node.data();
        DashboardComponent.tappedLink = null;
        node.addClass('multi');
        node.select();
        } else if (edge && edge.nonempty() && !edge?.hasClass('hidden')) {
        DashboardComponent.tappedNode = null;
        DashboardComponent.tappedLink = edge.data();
        edge.addClass('highlighted');
        edge.select();
      } else {
        DashboardComponent.tappedNode = null;
        DashboardComponent.tappedLink = null;
      }

      this.boxSelectionElements = [];
      this.boxEndPosition = null;
    });
  }


  private handleDragSelectionEnd(cy: Core, shiftPressed?: boolean) {
    const draggedElements = this.boxSelectionElements.map((e: any) => e.data());
    // Prefer the real event's shiftKey (passed by the caller) over the
    // tracked static flag, which can get stuck true if a keyup is missed.
    const isShiftPressed = shiftPressed ?? ButtonPressService.isShiftPressed;

    if (isShiftPressed) {
      this.handleShiftDragSelection(draggedElements, cy);
    } else {
      this.handleRegularDragSelection(draggedElements, cy);
    }
  }

  private handleShiftDragSelection(draggedElements: any[], cy: Core) {
    const selectedElements = [...DashboardComponent.multiSelectedElements];
    const newlySelectedElements: any[] = [];

    draggedElements.forEach(element => {
      const isCurrentlySelected = selectedElements.some((e: any) => e.id === element.id);
      const cyElement = cy.getElementById(element.id);

      if (isCurrentlySelected) {
        cyElement.addClass('force-unselect');
        cyElement.unselect();
      } else {
        const existingElement = DashboardComponent.multiSelectedElements.find((e: any) => e.id === element.id);
        if (!existingElement) {
          DashboardComponent.multiSelectedElements.push(element);
          newlySelectedElements.push(element);
        }
        cyElement.select();
      }
    });

    if (newlySelectedElements.length > 0) {
      this.setFocusToClosestElement(newlySelectedElements, cy);
    }
  }

  private setFocusToClosestElement(elements: any[], cy: Core) {
    if (elements.length === 0) return;

    if (elements.length === 1) {
      this.setDragSelectionFocus(elements[0]);
      return;
    }

    const dragReleasePoint = this.boxEndPosition || { x: 0, y: 0 };
    let closestElement = elements[0];
    let closestDistance = Infinity;

    elements.forEach(element => {
      const cyElement = cy.getElementById(element.id);
      const position = cyElement.position();
      const distance = Math.sqrt(
        Math.pow(position.x - dragReleasePoint.x, 2) +
        Math.pow(position.y - dragReleasePoint.y, 2)
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = element;
      }
    });

    this.setDragSelectionFocus(closestElement);
  }

  private handleRegularDragSelection(draggedElements: any[], cy: Core) {
    DashboardComponent.multiSelectedElements = draggedElements;

    const firstElement = first(this.boxSelectionElements);
    if (firstElement) {
      this.setDragSelectionFocus(firstElement.data());
      firstElement.select();
    }
  }

  private setDragSelectionFocus(element: any) {
    setTimeout(() => {
      const cyElement = this.cy?.getElementById(element.id);
      const isNode = this.isElementNode(element);
      if (isNode) {
        DashboardComponent.tappedNode = element;
        DashboardComponent.tappedLink = null;
        cyElement!.addClass('multi');
      } else {
        DashboardComponent.tappedLink = element;
        DashboardComponent.tappedNode = null;
        cyElement!.addClass('highlighted');
      }
    }, 400)
  }

  private isElementNode(element: any): boolean {
    return element && 'children' in element && !('from' in element) && !('to' in element);
  }

  private handleForcePan() {
    if (!this.cy) return;
    fromEvent<MouseEvent>(document, 'mousemove').subscribe((event) => {
      if (!this.cy) return;
      if (event.ctrlKey) {
        const dx = event.movementX;
        const dy = event.movementY;

        // Adjust the pan sensitivity as needed
        const panSensitivity = 1;
        // @ts-ignore
        document.documentElement.style.cursor = 'grab';
        // Pan the Cytoscape viewport
        this.cy.panBy({ x: dx * panSensitivity, y: dy * panSensitivity });

      } else {
        // @ts-ignore
        document.documentElement.style.cursor = 'auto';
      }
    });
  }

  /**
   * Custom box selection triggered by Alt/Option key
   */
  private handleAltBoxSelection() {
    if (!this.cy) return;
    const container = this.cy.container();
    if (!container) return;

    // Container-level mousedown listener (will be replaced when container changes).
    // Registered on capture so it runs before cytoscape's own mousedown handler,
    // which is bound to this same container element for bubble-phase 'grab'
    // hit-testing — a bubble-phase listener here would run after cytoscape's
    // and stopPropagation() would already be too late to stop a node grab.
    container.addEventListener('mousedown', (event: MouseEvent) => {
      if (!this.cy || !event.altKey) return;

      // Only start box selection if the mousedown DOM target is the cytoscape
      // container/canvas itself (not some other floating UI over it).
      const target = event.target as HTMLElement;
      if (target !== container && !target.classList.contains('cy-panzoom-slider-background')) {
        // Check if we clicked on the canvas itself
        const canvas = container.querySelector('canvas');
        if (target !== canvas && !container.contains(target)) return;
      }

      // Clean up any existing box element first (prevents orphaned elements)
      if (this.altBoxElement) {
        this.altBoxElement.remove();
        this.altBoxElement = null;
      }

      // Disable panning during box selection
      this.cy.userPanningEnabled(false);

      event.preventDefault();
      event.stopPropagation();
      this.isAltBoxSelecting = true;
      this.altBoxStartPos = {x: event.clientX, y: event.clientY};
      this.altBoxLiveSelectedIds = new Set();

      // resetAllSelected empties the list first, so 'unselect' cannot re-select.
      if (!event.shiftKey) {
        this.resetAllSelected();
      }

      // Create selection box element (hidden until mouse moves)
      this.altBoxElement = document.createElement('div');
      this.altBoxElement.style.position = 'fixed';
      this.altBoxElement.style.border = '1px dashed #666';
      this.altBoxElement.style.backgroundColor = 'rgba(100, 100, 100, 0.1)';
      this.altBoxElement.style.pointerEvents = 'none';
      this.altBoxElement.style.zIndex = '10000';
      this.altBoxElement.style.left = `${event.clientX}px`;
      this.altBoxElement.style.top = `${event.clientY}px`;
      this.altBoxElement.style.width = '0px';
      this.altBoxElement.style.height = '0px';
      this.altBoxElement.style.display = 'none';
      document.body.appendChild(this.altBoxElement);
    }, {capture: true});

    // Only add document-level listeners once
    if (this.altBoxListenersAdded) return;
    this.altBoxListenersAdded = true;

    this.altBoxMouseMoveHandler = (event: MouseEvent) => {
      if (!this.isAltBoxSelecting || !this.altBoxStartPos || !this.altBoxElement || !this.cy) return;

      const startX = this.altBoxStartPos.x;
      const startY = this.altBoxStartPos.y;
      const currentX = event.clientX;
      const currentY = event.clientY;

      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - startY);

      // Only show the box once there's actual movement (avoids dot on click)
      if (width > 2 || height > 2) {
        this.altBoxElement.style.display = 'block';
      }

      const left = Math.min(startX, currentX);
      const top = Math.min(startY, currentY);

      this.altBoxElement.style.left = `${left}px`;
      this.altBoxElement.style.top = `${top}px`;
      this.altBoxElement.style.width = `${width}px`;
      this.altBoxElement.style.height = `${height}px`;

      // Live-update the actual selection as the rectangle grows/shrinks, so
      // elements are added to or removed from it in real time rather than
      // only once at mouseup. Skipped during a shift-drag, which has its own
      // toggle-existing-selection semantics applied once at mouseup below.
      if (!event.shiftKey) {
        const modelBox = this.altBoxClientRectToModelBox(this.altBoxStartPos, {x: currentX, y: currentY});
        if (modelBox) {
          const matches = this.getElementsInModelBox(modelBox);
          const matchedIds = new Set(matches.map((ele: any) => ele.id()));

          matchedIds.forEach((id) => {
            if (!this.altBoxLiveSelectedIds.has(id)) {
              this.cy!.getElementById(id).select();
            }
          });
          this.altBoxLiveSelectedIds.forEach((id) => {
            if (!matchedIds.has(id)) {
              this.cy!.getElementById(id).unselect();
            }
          });
          this.altBoxLiveSelectedIds = matchedIds;
        }
      }
    };

    this.altBoxMouseUpHandler = (event: MouseEvent) => {
      if (!this.isAltBoxSelecting || !this.altBoxStartPos || !this.cy) {
        this.cleanupAltBoxSelection();
        return;
      }

      const modelBox = this.altBoxClientRectToModelBox(this.altBoxStartPos, {x: event.clientX, y: event.clientY});

      // Apply selection
      const selectedElements = modelBox ? this.getElementsInModelBox(modelBox) : [];
      if (selectedElements.length > 0) {
        this.boxSelectionElements = selectedElements;
        this.handleDragSelectionEnd(this.cy, event.shiftKey);
        this.boxSelectionElements = [];
      }

      this.cleanupAltBoxSelection();
    };

    document.addEventListener('mousemove', this.altBoxMouseMoveHandler);
    document.addEventListener('mouseup', this.altBoxMouseUpHandler);
  }

  private altBoxClientRectToModelBox(
    startClient: { x: number; y: number },
    currentClient: { x: number; y: number }
  ): { x1: number; y1: number; x2: number; y2: number } | null {
    if (!this.cy) return null;
    const container = this.cy.container();
    if (!container) return null;

    const containerRect = container.getBoundingClientRect();
    const pan = this.cy.pan();
    const zoom = this.cy.zoom();

    const boxLeft = Math.min(startClient.x, currentClient.x) - containerRect.left;
    const boxTop = Math.min(startClient.y, currentClient.y) - containerRect.top;
    const boxRight = Math.max(startClient.x, currentClient.x) - containerRect.left;
    const boxBottom = Math.max(startClient.y, currentClient.y) - containerRect.top;

    return {
      x1: (boxLeft - pan.x) / zoom,
      y1: (boxTop - pan.y) / zoom,
      x2: (boxRight - pan.x) / zoom,
      y2: (boxBottom - pan.y) / zoom
    };
  }

  private getElementsInModelBox(modelBox: { x1: number; y1: number; x2: number; y2: number }): any[] {
    if (!this.cy) return [];
    const optimizeLinkSelection = this.settingsService.getOptimizeLinkSelection();
    const matches: any[] = [];

    this.cy.nodes().forEach((node: any) => {
      if (node.hidden()) return;
      const bb = node.boundingBox({includeLabels: false});
      if (bb.x2 >= modelBox.x1 && bb.x1 <= modelBox.x2 && bb.y2 >= modelBox.y1 && bb.y1 <= modelBox.y2) {
        matches.push(node);
      }
    });

    this.cy.edges().forEach((edge: any) => {
      if (edge.hidden()) return;
      if (this.edgeIntersectsModelBox(edge, modelBox, optimizeLinkSelection)) {
        matches.push(edge);
      }
    });

    return matches;
  }

  /**
   * Tests a link against the selection box using its shape (source/target
   * plus any bend/control points) rather than its bounding box, so a long
   * diagonal link no longer gets swept in just because its bbox happens to
   * cover the box even though the line itself is nowhere near it. When
   * `optimizeLinkSelection` is on (default), the shape is simplified to the
   * straight line between the endpoint centers.
   */
  private edgeIntersectsModelBox(
    edge: any,
    box: { x1: number; y1: number; x2: number; y2: number },
    optimizeLinkSelection: boolean
  ): boolean {
    const points = this.getEdgeShapePoints(edge, optimizeLinkSelection);
    for (let i = 0; i < points.length - 1; i++) {
      if (this.segmentIntersectsRect(points[i], points[i + 1], box)) {
        return true;
      }
    }
    return false;
  }

  private getEdgeShapePoints(edge: any, optimizeLinkSelection: boolean): { x: number; y: number }[] {
    const sourcePos = edge.source().position();
    const targetPos = edge.target().position();
    if (optimizeLinkSelection) {
      return [sourcePos, targetPos];
    }

    const type = edge.data('type');
    const explicitPoints = type === 'UNBUNDLED_BEZIER'
      ? edge.data('controlPointPositions')
      : type === 'STRAIGHT'
        ? edge.data('bendPointPositions')
        : null;

    if (Array.isArray(explicitPoints) && explicitPoints.length > 0) {
      return [sourcePos, ...explicitPoints, targetPos];
    }
    // BEZIER (default auto-curve) has no stored anchor points; a straight
    // line between endpoints is a close enough approximation of its gentle
    // default curve for hit-testing purposes.
    return [sourcePos, targetPos];
  }

  private segmentIntersectsRect(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    rect: { x1: number; y1: number; x2: number; y2: number }
  ): boolean {
    const pointInRect = (p: { x: number; y: number }) =>
      p.x >= rect.x1 && p.x <= rect.x2 && p.y >= rect.y1 && p.y <= rect.y2;

    if (pointInRect(p1) || pointInRect(p2)) return true;

    const corners = [
      {x: rect.x1, y: rect.y1},
      {x: rect.x2, y: rect.y1},
      {x: rect.x2, y: rect.y2},
      {x: rect.x1, y: rect.y2}
    ];
    for (let i = 0; i < 4; i++) {
      const edgeStart = corners[i];
      const edgeEnd = corners[(i + 1) % 4];
      if (this.segmentsIntersect(p1, p2, edgeStart, edgeEnd)) return true;
    }
    return false;
  }

  private segmentsIntersect(
    a1: { x: number; y: number }, a2: { x: number; y: number },
    b1: { x: number; y: number }, b2: { x: number; y: number }
  ): boolean {
    const cross = (ox: number, oy: number, ax: number, ay: number, bx: number, by: number) =>
      (ax - ox) * (by - oy) - (ay - oy) * (bx - ox);

    const d1 = cross(b1.x, b1.y, b2.x, b2.y, a1.x, a1.y);
    const d2 = cross(b1.x, b1.y, b2.x, b2.y, a2.x, a2.y);
    const d3 = cross(a1.x, a1.y, a2.x, a2.y, b1.x, b1.y);
    const d4 = cross(a1.x, a1.y, a2.x, a2.y, b2.x, b2.y);

    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
  }

  private cleanupAltBoxSelection() {
    this.isAltBoxSelecting = false;
    this.altBoxStartPos = null;
    this.altBoxLiveSelectedIds = new Set();
    if (this.altBoxElement) {
      this.altBoxElement.remove();
      this.altBoxElement = null;
    }
    // Re-enable panning after box selection
    if (this.cy) {
      this.cy.userPanningEnabled(true);
    }
  }

  private onNodeDragFree(event: cytoscape.EventObject): void {
    this.draggedDistance = 0;
    if (!this.cy) return;
    if (!this.mapStateService.isDefaultState()) {
      this.cy.elements().removeClass('grabbed');
      return;
    }

    if (!this.dragStarted) {
      const draggedNode = event.target;
      draggedNode.removeClass('grabbed');
      draggedNode.parents().removeClass('grabbed');
      return;
    }

    const draggedNode = event.target;
    draggedNode.removeClass('grabbed');
    draggedNode.parents().removeClass('grabbed');

    if (this.linkingCompleteSubject.getValue()) {
      this.linkingCompleteSubject.next(false);
    }
    if (draggedNode.data('draggedByParent')) return; // Skip if dragged by parent

    // The `free` event always carries the release cursor position, so re-resolve the
    // drop target here. While a snap plugin holds the node locked cytoscape stops
    // emitting positioned `drag` events, which would otherwise leave a stale target.
    this.updateDragHoverState(event);

    // Find the highlighted node (potential drop target)
    const highlightedNode = this.cy.nodes('.highlighted').first();
    const allSelectedNodes = this.cy.nodes(':selected');

    if (highlightedNode.nonempty()) {
      // Handle the drop logic with highlightedNode as the target - this is a container change
      const updatedNodesData = [];
      const containerChanges: ContainerChangeSnapshot[] = [];

      if (allSelectedNodes.length > 1) {
        // Handle multiple nodes being dragged
        // Filter out nodes that are descendants of other selected nodes to preserve containment hierarchy
        const selectedNodeIds = new Set(allSelectedNodes.map((n: any) => n.id()));
        const topLevelSelectedNodes = allSelectedNodes.filter((selectedNode: any) => {
          // Check if any ancestor of this node is also in the selection
          let ancestor = selectedNode.parent();
          while (ancestor.nonempty()) {
            if (selectedNodeIds.has(ancestor.id())) {
              // This node's ancestor is selected, so skip it (it will move with its parent)
              return false;
            }
            ancestor = ancestor.parent();
          }
          return true;
        });

        topLevelSelectedNodes.forEach((selectedNode: any) => {
          const beforeParentId = selectedNode.parent().nonempty() ? selectedNode.parent().first().id() : null;
          const beforePos = selectedNode.data('grabbedPosition') || selectedNode.position();

          containerChanges.push({
            nodeId: selectedNode.id(),
            beforeParentId,
            afterParentId: highlightedNode.id(),
            beforePosition: {x: beforePos.x, y: beforePos.y},
            afterPosition: {...selectedNode.position()}
          });

          selectedNode.move({ parent: highlightedNode.id() });
          updatedNodesData.push({
            childIds: selectedNode.parent().children().map((child: any) => child.id()),
            parentId: highlightedNode.id()
          });
        });
      } else {
        const beforeParentId = draggedNode.parent().nonempty() ? draggedNode.parent().first().id() : null;
        const beforePos = draggedNode.data('grabbedPosition') || draggedNode.position();

        containerChanges.push({
          nodeId: draggedNode.id(),
          beforeParentId,
          afterParentId: highlightedNode.id(),
          beforePosition: {x: beforePos.x, y: beforePos.y},
          afterPosition: {...draggedNode.position()}
        });

        draggedNode.move({ parent: highlightedNode.id() });
        updatedNodesData.push({
          childIds: draggedNode.parent().children().map((child: any) => child.id()),
          parentId: draggedNode.parent().id()
        });
      }

      // Create ChangeContainerCommand for undo/redo (don't execute, action already done)
      if (containerChanges.length > 0) {
        const containerCommand = new ChangeContainerCommand(
            this,
            this.nodeService,
            this.mapService,
            'change-container',
            containerChanges
        );
        this.undoRedoManager.pushCommand(containerCommand);
      }

      from(updatedNodesData)
        .pipe(
          concatMap((data) => this.nodeService.updateParent(data))
        )
        .subscribe();

      if (highlightedNode.hasClass('collapsed')) {
        //@ts-ignore
        const api = this.cy.expandCollapse('get');
        highlightedNode.data('collapsed', false);
        api.expand(highlightedNode);
        highlightedNode.addClass('expanded').removeClass('collapsed');
      }
    } else {
      // No container change - track position changes for MoveCommand
      const moveSnapshots: MoveSnapshot[] = [];

      if (allSelectedNodes.length > 1) {
        // Handle multiple selected nodes
        allSelectedNodes.forEach((node: any) => {
          const grabbedPos = node.data('grabbedPosition');
          const currentPos = node.position();
          if (grabbedPos && (grabbedPos.x !== currentPos.x || grabbedPos.y !== currentPos.y)) {
            moveSnapshots.push({
              nodeId: node.id(),
              before: {x: grabbedPos.x, y: grabbedPos.y},
              after: {x: currentPos.x, y: currentPos.y}
            });
          }
        });
      } else {
        const grabbedPos = draggedNode.data('grabbedPosition');
        const currentPos = draggedNode.position();
        if (grabbedPos && (grabbedPos.x !== currentPos.x || grabbedPos.y !== currentPos.y)) {
          moveSnapshots.push({
            nodeId: draggedNode.id(),
            before: {x: grabbedPos.x, y: grabbedPos.y},
            after: {x: currentPos.x, y: currentPos.y}
          });
        }
      }

      // Create MoveCommand if there were actual position changes
      if (moveSnapshots.length > 0) {
        // Capture edge control points after the move
        const edgesBefore = CytoscapeService.grabbedEdgeControlPoints;
        const edgesAfter = this.captureCurrentEdgeControlPoints();

        const moveCommand = new MoveCommand(
            this,
            this.nodeService,
            moveSnapshots,
            edgesBefore,
            edgesAfter
        );
        this.undoRedoManager.pushCommand(moveCommand);

        // Clear the grabbed edge control points
        CytoscapeService.grabbedEdgeControlPoints = [];
      }
    }

    // Remove highlighting and any additional cleanup
    highlightedNode.removeClass('highlighted');
    if (allSelectedNodes.length > 0) {
      allSelectedNodes.forEach((n) => this.unmarkChildIfDraggedByParent(n));
    }
    this.unmarkChildIfDraggedByParent(draggedNode);

    this.dragStarted = false;

    //If we release a dragged container , we should select it
    if (draggedNode.isParent()) {
      draggedNode.select();
      DashboardComponent.tappedNode = draggedNode.data();
    }
  }

  private onGrabNode(event: cytoscape.EventObject): void {
    if (!this.cy) return;
    const node = event.target;

    // MAP-1270: Handle shift-selection at grab time (before drag starts).
    // Prefer the live originalEvent.shiftKey: the static flag can get stuck
    // true if a keyup is missed (e.g. focus lost while Shift was held), and
    // ORing it in would keep treating every grab as shift-held regardless of
    // the real modifier state. Only fall back to the static flag when there's
    // no real event backing this call (a synthetic/programmatic grab).
    const isShiftPressed = (event.originalEvent as MouseEvent)?.shiftKey ?? ButtonPressService.isShiftPressed;

    // Only clear followers if this is NOT a secondary grab event during shift-drag
    // (When we select a node during shift-grab, Cytoscape fires another grab for that node)
    const isSecondaryGrab = isShiftPressed && node.selected() && CytoscapeService.shiftDragFollowers.size > 0;
    if (!isSecondaryGrab) {
      CytoscapeService.shiftDragFollowers.clear();
    }

    if (isShiftPressed && !node.selected()) {
      const selectedElements = this.cy.$(':selected').filter('node');
      if (selectedElements.length > 0) {
        // Store positions of previously selected nodes - they need to follow the grabbed node
        selectedElements.forEach((el: cytoscape.NodeSingular) => {
          const pos = el.position();
          CytoscapeService.shiftDragFollowers.set(el.id(), {x: pos.x, y: pos.y});
        });

        // Add grabbed node to selection
        node.select();

        // Update multiSelectedElements
        const elementData = node.data();
        selectedElements.forEach((el: any) => {
          const data = el.data();
          if (!DashboardComponent.multiSelectedElements.some((e: any) => e.id === data.id)) {
            DashboardComponent.multiSelectedElements.push(data);
          }
        });
        if (!DashboardComponent.multiSelectedElements.some((e: any) => e.id === elementData.id)) {
          DashboardComponent.multiSelectedElements.push(elementData);
        }

        // Set focus to grabbed node
        DashboardComponent.tappedNode = elementData;
        DashboardComponent.tappedLink = null;

        // Mark that shift-grab occurred so tap handler won't toggle selection
        CytoscapeService.shiftGrabOccurred = true;
      }
    }

    this.addGrabbedPositionToAllDescendants(node);
    this.markChildIfDraggedByParent(node);

    // Capture edge control points for connected edges (for undo/redo)
    this.captureConnectedEdgeControlPoints(node);
  }

  /**
   * Capture control point data for all edges connected to the given node(s).
   * This is called on grab to store "before" state for undo/redo.
   */
  private captureConnectedEdgeControlPoints(node: cytoscape.NodeSingular): void {
    if (!this.cy) return;

    CytoscapeService.grabbedEdgeControlPoints = [];
    const processedEdges = new Set<string>();

    // Get all nodes that might move (the grabbed node and all selected nodes)
    const allSelectedNodes = this.cy.$(':selected').filter('node');
    const nodesToProcess = allSelectedNodes.length > 1 ? allSelectedNodes : this.cy.collection().add(node);

    // For each node, get connected edges
    nodesToProcess.forEach((n: cytoscape.NodeSingular) => {
      const connectedEdges = n.connectedEdges();
      connectedEdges.forEach((edge: cytoscape.EdgeSingular) => {
        const edgeId = edge.id();
        if (processedEdges.has(edgeId)) return;
        processedEdges.add(edgeId);

        const snapshot = this.captureEdgeControlPointSnapshot(edge);
        if (snapshot) {
          CytoscapeService.grabbedEdgeControlPoints.push(snapshot);
        }
      });
    });
  }

  /**
   * Capture control point data for a single edge.
   * Uses deep copy to ensure captured data is immutable.
   */
  private captureEdgeControlPointSnapshot(edge: cytoscape.EdgeSingular): EdgeControlPointSnapshot | null {
    const edgeData = edge.data();

    // Check for any edge control data (positions or weights/distances)
    const hasBendPoints = edgeData.bendPointPositions?.length > 0;
    const hasControlPoints = edgeData.controlPointPositions?.length > 0;
    const hasBendWeights = edgeData.cyedgebendeditingWeights?.length > 0;
    const hasControlWeights = edgeData.cyedgecontroleditingWeights?.length > 0;

    if (!hasBendPoints && !hasControlPoints && !hasBendWeights && !hasControlWeights) {
      return null;
    }

    return {
      edgeId: edge.id(),
      bendPointPositions: edgeData.bendPointPositions ? structuredClone(edgeData.bendPointPositions) : null,
      controlPointPositions: edgeData.controlPointPositions ? structuredClone(edgeData.controlPointPositions) : null,
      cyedgebendeditingDistances: edgeData.cyedgebendeditingDistances ? structuredClone(edgeData.cyedgebendeditingDistances) : null,
      cyedgebendeditingWeights: edgeData.cyedgebendeditingWeights ? structuredClone(edgeData.cyedgebendeditingWeights) : null,
      cyedgecontroleditingDistances: edgeData.cyedgecontroleditingDistances ? structuredClone(edgeData.cyedgecontroleditingDistances) : null,
      cyedgecontroleditingWeights: edgeData.cyedgecontroleditingWeights ? structuredClone(edgeData.cyedgecontroleditingWeights) : null
    };
  }

  /**
   * Capture current control point data for edges that were tracked on grab.
   * Called after move to get "after" state.
   */
  private captureCurrentEdgeControlPoints(): EdgeControlPointSnapshot[] {
    if (!this.cy) return [];

    const snapshots: EdgeControlPointSnapshot[] = [];

    for (const beforeSnapshot of CytoscapeService.grabbedEdgeControlPoints) {
      const edge = this.cy.$(`#${beforeSnapshot.edgeId}`);
      if (edge.length > 0) {
        const snapshot = this.captureEdgeControlPointSnapshot(edge);
        if (snapshot) {
          snapshots.push(snapshot);
        }
      }
    }

    return snapshots;
  }

  getSafeLayoutOptions(layoutName: string, cy: cytoscape.Core): cytoscape.LayoutOptions {
    const avgEdgeWeight = this.average(cy.edges().map(e => e.data('layoutWeight') || 1));
    const avgNodeWeight = this.average(cy.nodes().map(n => n.data('layoutWeight') || 1));

    const common = {
      name: layoutName,
      animate: true,
      animationDuration: 500
    };

    const dynamicLayouts = ['fcose', 'cola', 'euler']; // layouts that accept functions

    if (dynamicLayouts.includes(layoutName)) {
      return {
        ...common,
        // @ts-ignore
        edgeElasticity: (edge: any) => (edge.data('layoutWeight') || 1) * 10,
        nodeRepulsion: (node: any) => (node.data('layoutWeight') || 1) * 1000,
        idealEdgeLength: (edge: any) => 120 / (edge.data('layoutWeight') || 1)
      };
    }

    // fallback for static-only layouts like cose-bilkent
    return {
      ...common,
      // @ts-ignore
      edgeElasticity: avgEdgeWeight * 10,
      nodeRepulsion: avgNodeWeight * 1000,
      idealEdgeLength: 120 / avgEdgeWeight
    };
  }

  average(values: number[]): number {
    if (values.length === 0) return 1;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  }

  public getNodeDataBasedOnState(node: NodeSingular): GraphNode {
    const currentMapState = this.mapStateService.getStateSignal();
    const isDefaultState = !currentMapState || currentMapState?.id === undefined;
    let nodeData = node.data() as GraphNode;
    if (!isDefaultState) {
      const getStatedNode = nodeData?.states?.find((s: NodeStateData) => s.stateId === currentMapState?.id);
      if (getStatedNode) {
        nodeData = getStatedNode?.node as unknown as GraphNode;
      } else {
        nodeData = node.data() as GraphNode;
      }
    }
    return nodeData;
  }

  public hasLinkDataControlOrSegmentPoints(data: GraphNodeLink): boolean {
    //@ts-ignore
    return data?.bendPointPositions?.length > 0 || data?.controlPointPositions?.length > 0;
  }

  public getLinkDataBasedOnState(link: EdgeSingular, tappedLink?: GraphNodeLink | null): GraphNodeLink {
    const currentMapState = this.mapStateService.getStateSignal();
    const isDefaultState = !currentMapState || currentMapState?.id === undefined;
    let linkData = tappedLink || link.data() as GraphNodeLink;
    if (!isDefaultState) {
      const getStatedLink = linkData.states?.find((s: LinkStateData) => s.stateId === currentMapState?.id);
      if (getStatedLink) {
        linkData = getStatedLink?.link as unknown as GraphNodeLink;
      } else {
        linkData = link.data() as GraphNodeLink;
      }
    }
    return linkData;
  }

  private hasMapPermission(permission: Permission): boolean {
    const map = this.mapService.selectedMap();
    return this.mapRoleService.hasCurrentUserPermission(permission, map);
  }

  /** Viewers have no comments section, so the canvas must not hint at one. */
  public canSeeComments(): boolean {
    if (isInIframe()) {
      return !!this.mapService.selectedMap()?.hasGuestComments;
    }
    return this.hasMapPermission('comment');
  }

  public containsUrl(value: string): boolean {
    if (!value) return false;

    // Markdown links [text](url), anywhere in the value.
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    while ((match = markdownLinkRegex.exec(value)) !== null) {
      try {
        new URL(match[2]);
        return true;
      } catch {
        continue;
      }
    }

    // MAP-1530: a plain URL with text before/after it (e.g. "Go to
    // https://google.com") must still count, so scan each whitespace-separated
    // token instead of requiring the whole value to parse as a URL. This also
    // covers the whole-value case (a single token).
    for (const token of value.split(/\s+/)) {
      if (!token) continue;
      try {
        new URL(token);
        return true;
      } catch {
        continue;
      }
    }

    return false;
  }

  public initializeCytoscape(params: {
    cytoscapeContainer: ElementRef;
    currentMap: GraphMap | undefined;
    isReadOnly: boolean;
    isChangingLinkSource: boolean;
    settings: Partial<GeneralInfo>;
    cyService: any;
    componentInstance: any;
  }) {
    this.cy = cytoscape({
      container: params.cytoscapeContainer?.nativeElement,
      maxZoom: CytoscapeService.MAX_ZOOM_LEVEL,
      webgl: this.settingsService.getLocalStorageSettings().webgl ?? false,
      minZoom: CytoscapeService.MIN_ZOOM_LEVEL,
      textureOnViewport: this.settingsService.getLocalStorageSettings().textureRenderer ?? true,
      boxSelectionEnabled: false, // We handle box selection manually with Alt/Option key
      autolock:
        (params.currentMap?.isReadonly || isInIframe()) &&
        !this.hasMapPermission('edit'),
      style: [
        {
          selector: 'node',
          style: {
            'background-color': (node: any) => {
              const nodeData = this.getNodeDataBasedOnState(node);

              const color = nodeData?.color || DefaultValues.node.color;
              const nodeOpacity = nodeData?.properties?.opacity;
              if (nodeOpacity === 0 || nodeOpacity === '0') {
                const map = this.mapService.getSelectedMapSignal();
                const isDark = this.settingsService.isDarkMode(
                  map?.styles?.appearance,
                );
                return isDark ? '#000' : '#fff';
              }
              const trueOpacity =
                nodeOpacity !== undefined && nodeOpacity !== null
                  ? (nodeOpacity as number)
                  : (DefaultValues.node.properties.opacity as number);

              // Use HSL transformation for better color perception
              // Dynamic target luminance based on appearance mode
              const map = this.mapService.getSelectedMapSignal();
              const isDark = this.settingsService.isDarkMode(
                map?.styles?.appearance,
              );
              const targetLuminance = isDark ? 0.0 : 1.0; // Black for dark mode, white for light mode
              return transformColorWithHSL(
                color,
                targetLuminance,
                trueOpacity, // Intensity factor (use opacity directly)
              );
            },
            shape: (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              return cyNodeShape(data.type);
            },
            'background-image': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              return data.imageUrl
                ? environment.apiUrl + '/public/image?url=' + encodeURIComponent(data.imageUrl)
                : 'default';
            },
            'background-opacity': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              const defaultOpacity = DefaultValues.node.properties.colorOpacity;
              const propertyOpacity = data.properties?.colorOpacity;
              const opacity =
                propertyOpacity !== undefined && propertyOpacity !== null
                  ? propertyOpacity
                  : defaultOpacity;
              return (opacity > 1 ? 1 : opacity) * nodeDimFactor(node);
            },
            'background-image-opacity': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              const defaultOpacity =
                this.settingsService.getLocalStorageSettings()
                  ?.defaultImageOpacity || DefaultNodeProperties.imageOpacity;
              const propertyOpacity = data.properties?.imageOpacity;
              const opacity =
                propertyOpacity !== undefined && propertyOpacity !== null
                  ? propertyOpacity
                  : defaultOpacity;
              return (opacity > 1 ? 1 : opacity) * nodeDimFactor(node);
            },
            'background-fit': (node: NodeSingular) =>
              this.nodeImageBackgroundSize(node) ? 'none' : 'cover',
            'background-width': (node: NodeSingular) =>
              this.nodeImageBackgroundSize(node)?.width ?? 'auto',
            'background-height': (node: NodeSingular) =>
              this.nodeImageBackgroundSize(node)?.height ?? 'auto',
            width: (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              const scale =
                data.properties?.scale === '0' || data.properties?.scale === 0
                  ? 0.25
                  : data.properties?.scale;
              return scale !== undefined && scale !== null ? scale * 10 : 10;
            },
            height: (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              const scale =
                data.properties?.scale === '0' || data.properties?.scale === 0
                  ? 0.25
                  : data.properties?.scale;
              return scale !== undefined && scale !== null ? scale * 10 : 10;
            },
            // See cytoscape-dash.helper for why dotted maps to 'dashed'.
            'border-style': (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              return cytoscapeStrokeStyle(data.properties?.borderStyle);
            },
            // Cytoscape cannot consume an empty array, so return its default.
            //@ts-ignore
            'border-dash-pattern': (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              return strokeDashPattern(
                data.properties?.borderStyle,
                this.nodeBorderWidth(node)
              ) ?? [4, 2];
            },
            //@ts-ignore
            'border-cap': (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              return strokeLineCap(data.properties?.borderStyle);
            },
            //@ts-ignore
            'border-join': 'round',
            'border-width': (node: any) => `${this.nodeBorderWidth(node)}px`,
            'border-color': (node: NodeSingular) => {
              const nodeData = this.getNodeDataBasedOnState(node);
              const bottleneckMeta = (nodeData as any)?.metadata?.find((m: any) => m.key === 'bottleneck');
              if (bottleneckMeta) {
                return bottleneckMeta.value === 'high' ? '#d32f2f' : bottleneckMeta.value === 'medium' ? '#f57c00' : '#fbc02d';
              }
              return nodeData?.color || DefaultValues.node.color;
            },
            opacity: (node: NodeSingular) => {
              const currentStateNode = this.getNodeDataBasedOnState(node);
              if (currentStateNode.excludedFromState) {
                // MAP-1017: was hardcoded 0.4, now the configurable filtering opacity.
                return SettingsService.sharedValues.filteringOpacity;
              } else {
                return 1;
              }
            },
            'z-index': 3,
            'z-index-compare': 'manual',
            visibility: (node: NodeSingular) => {
              const isEditMode = this.sidebarService.getEditModeSignal();
              const hasEditPermission =
                this.hasMapPermission('edit') ||
                this.hasMapPermission('manage');
              if (
                !isEditMode ||
                isInIframe() ||
                (params.isReadOnly && !hasEditPermission)
              ) {
                const currentStateNode = this.getNodeDataBasedOnState(node);
                return currentStateNode.excludedFromState
                  ? 'hidden'
                  : 'visible';
              } else {
                return 'visible';
              }
            },
          },
        },
        {
          selector: 'node:parent',
          style: {
            shape: 'roundrectangle',
            'background-color': (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              const color = data.color || DefaultValues.node.color;
              const nodeOpacity = data.properties?.opacity;
              if (nodeOpacity === 0 || nodeOpacity === '0') {
                const map = this.mapService.getSelectedMapSignal();
                const isDark = this.settingsService.isDarkMode(
                  map?.styles?.appearance,
                );
                return isDark ? '#000' : '#fff';
              }
              const trueOpacity =
                nodeOpacity !== undefined && nodeOpacity !== null
                  ? nodeOpacity
                  : DefaultValues.node.properties.opacity;

              // Use HSL transformation for better color perception
              // Dynamic target luminance based on appearance mode
              const map = this.mapService.getSelectedMapSignal();
              const isDark = this.settingsService.isDarkMode(
                map?.styles?.appearance,
              );
              const targetLuminance = isDark ? 0.0 : 1.0; // Black for dark mode, white for light mode
              return transformColorWithHSL(
                color,
                targetLuminance,
                trueOpacity, // Intensity factor (use opacity directly)
              );
            },
            'background-image': (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              return data.imageUrl
                ? environment.apiUrl + '/public/image?url=' + encodeURIComponent(data.imageUrl)
                : 'default';
            },
            padding: (node: NodeSingular) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              const padding = Number(data.properties?.padding);
              return data.properties?.padding !== undefined &&
                data.properties?.padding !== null &&
                data.properties?.padding !== '' &&
                Number.isFinite(padding)
                ? Math.max(0, padding)
                : DefaultValues.node.properties.padding;
            },
            'compound-sizing-wrt-labels': 'include',
            'z-index': 2,
            'z-index-compare': 'manual',
          },
        },
        {
          selector: 'node.grabbed',
          style: {
            'z-index': 2,
            'z-index-compare': 'manual',
            opacity: () => {
              return SettingsService.sharedValues.node.nodeDragOpacity;
            },
          },
        },
        {
          selector: 'node[label]',
          style: {
            label: (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              let name = '';
              if (data.properties?.label === 'name' || !data.metadata || !Array.isArray(data.metadata)) {
                name = data.name || '';
              } else {
                name =
                  data.metadata.find(
                    (p: any) => p.key === data.properties?.label,
                  )?.value || '';
              }
              const hasSomeUrlMetaValue = data.metadata?.some((m: any) => {
                return this.containsUrl(m.value);
              });
              if (hasSomeUrlMetaValue) {
                name = StatusIcons.HAS_LINKS + '\u00A0' + name;
              }
              if (data.hasComments && params.cyService.canSeeComments()) {
                name = name + '\u00A0' + StatusIcons.HAS_COMMENTS;
              }
              const isBottleneck = data.metadata?.some((m: any) => m.key === 'bottleneck');
              if (isBottleneck) {
                name = '\u26A0\uFE0F ' + name;
              }
              return name;
            },
            'font-family': 'Roboto, "maporium"',
            'text-valign': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              return data?.properties?.textValign || 'center';
            },
            'text-rotation': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              const angle = data?.properties?.textRotation || 0;
              return (angle * Math.PI) / 180;
            },
            'text-halign': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;
              return data.properties?.textHalign || 'center';
            },
            'text-margin-y': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              let val = data?.properties?.textMarginY;
              const isNotEmpty = val !== undefined && val !== null;

              if (data.properties?.textValign === 'top' && isNotEmpty) {
                val = val * -1;
              }

              return isNotEmpty
                ? val
                : DefaultValues.node.properties.textMarginY;
            },
            'text-margin-x': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              let val = data?.properties?.textMarginX;
              const isNotEmpty = val !== undefined && val !== null;
              if (
                data.properties?.textHalign === 'left' &&
                isNotEmpty &&
                val !== 0
              ) {
                val = val * -1;
              }
              return val !== undefined && val !== null
                ? val
                : DefaultValues.node.properties.textMarginX;
            },
            'font-size': (node: any) => {
              const data = this.getNodeDataBasedOnState(node) as any;

              const size = data?.properties?.textFontSize;
              return size !== undefined && size !== null && size !== ''
                ? size
                : DefaultValues.node.properties.textFontSize;
            },
            color: (node: any) => {
              const map = this.mapService.getSelectedMapSignal();
              const isDark = this.settingsService.isDarkMode(
                map?.styles?.appearance,
              );
              return isDark ? '#FFFFFF' : '#000000';
            },
            'text-wrap': 'wrap',
            'text-max-width': '999999px',
          },
        },
        //@ts-ignore
        ...this.getEdgeStyles(params),
        //@ts-ignore
        ...this.getAppearanceAwareCyNodeStyles(),
        //@ts-ignore
        ...miscStyles,
      ],
      layout: {
        name: 'preset',
      },
    });
    super.init(this.cy);

    // MAP-626: dimmed / translucent links must keep their directionality head in
    // the link's own colour instead of erasing the container fill under it.
    installTranslucentArrowheads(this.cy);
    installNodeImageClip(this.cy);
    this.installSelectionRefresh(this.cy);

    // Cleanup old link label renderer before creating new one
    if (this.linkLabelRenderer) {
      this.linkLabelRenderer.destroy();
      this.linkLabelRenderer = undefined;
    }

    // Link labels with performance optimizations (debounced pan/zoom)
    this.linkLabelRenderer = installHtmlEdgeLabels(this.cy, {
      getText: this.buildEdgeLabel.bind(this),
      getClasses: this.getEdgeLabelClasses.bind(this),
      getPos: (e: any) => {
        const d: any = e.data();
        const linkId = d?.id;
        // Use signal value if available, otherwise fall back to data
        const rawPosition = linkId ? this.getLinkLabelPosition(linkId, d?.linkLabelPosition ?? 0) : d?.linkLabelPosition ?? 0;
        // MAP-1559: clamp the rendered alignment so the label can't end up
        // inside a much bigger node's shape. The stored/slider value (rawPosition)
        // is left untouched - only the value handed to the renderer is remapped.
        return this.clampLinkLabelAlignmentForRendering(rawPosition, e);
      },
      getNormalOffset: (e: any) => {
        const d: any = e.data();
        const linkId = d?.id;
        // Use signal value if available, otherwise fall back to data (inverted)
        return linkId ? -this.getLinkLabelOffset(linkId, d?.linkLabelOffset ?? DefaultValues.link.linkLabelOffset) : -(d?.linkLabelOffset ?? DefaultValues.link.linkLabelOffset);
      },
      rotate: true,
      snapEpsilon: 0.08,
      // Custom filter: Always show labels for edges that have a name
      filter: (edge: any) => {
        return edge.visible() && !edge.hidden();
      },
    });

    // Set initial HTML edge label colors
    this.updateHtmlEdgeLabelColors();

    // Re-establish label position updates during pan/zoom (use fast position updates)
    // Use position-only updates for smooth pan/zoom performance
    if (this.linkLabelRenderer) {
      this.cy.on('zoom pan', () => {
        if (this.linkLabelRenderer) {
          this.linkLabelRenderer.updateLabelPositions();
        }
      });
    }

    // Refresh link labels after undo/redo operations
    // cy.batch() in commands doesn't emit grab/free events that the renderer listens to
    this.undoRedoManager.onCommandExecuted().subscribe(({action}) => {
      if (action === 'undo' || action === 'redo') {
        // Use setTimeout to ensure Cytoscape's batch operation has fully completed
        setTimeout(() => {
          if (this.linkLabelRenderer) {
            this.linkLabelRenderer.redraw();
          }
        }, 0);
      }
    });

    return this.cy;
  }

  private getEdgeStyles(params: any) {
    return [
      {
        selector: 'edge',
        style: {
          //@ts-ignore
          'curve-style': (element) => {
            const data = this.getLinkDataBasedOnState(element);
            //@ts-ignore
            return EdgeTypeMapCy[data.type || 'BEZIER'];
          },
          'target-arrow-shape': (element: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(element);
            const direction = data.direction;
            const arrowType = data.edgeArrowType || DefaultValues.link.edgeArrowType;
            return ((direction !== 'undirected' || direction === undefined) ? arrowType : 'none');
          },
          'source-arrow-shape': (element: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(element);

            const direction = data.direction;
            const arrowType = data.edgeArrowType || DefaultValues.link.edgeArrowType;
            return ((direction === 'bidirected') ? arrowType : 'none');
          },
          'line-color': (link: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(link);
            return data?.color || DefaultValues.link.color;
          },
          'target-arrow-color': (link: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(link);

            return data?.color || DefaultValues.link.color;
          },
          'source-arrow-color': (link: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(link);

            return data?.color || DefaultValues.link.color;
          },
          'arrow-scale': (link: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(link);
            const isGhostNode = Object.keys(data)?.length === 3;
            const weight = data.weight as number;

            // Use a logarithmic scale for smoother arrow growth
            // For weight ≥ 1, use a logarithmic function: 0.5 + 0.3*ln(weight)
            // This creates a more gradual arrow growth as weight increases
            return weight >= 1 ?
              0.5 + 0.3 * Math.log(weight) :
              isGhostNode ? 0.5 : 0.3;
          },
          // 'z-compound-depth': 'bottom',
          'width': (link: EdgeSingular) => this.linkLineWidth(link),
          'visibility': () => 'visible',
          'target-distance-from-node': (edge: EdgeSingular): number => {
            return 0;
          },
          'source-distance-from-node': (edge: EdgeSingular) => {
            return 0;
          },
          'target-endpoint': () => {
            return 'outside-to-node';
          },
          'source-endpoint': () => {
            return 'outside-to-node';
          },
          // 'z-index-compare': 'manual',
          // 'z-index': 5,
          'opacity': (link: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(link);
            if (data.excludedFromState) {
              const isEditMode = this.sidebarService.getEditModeSignal();
              const hasEditPermission = this.hasMapPermission('edit') || this.hasMapPermission('manage');

              if (isEditMode && !isInIframe() && hasEditPermission) {
                // MAP-1017: was hardcoded 0.4, now the configurable filtering opacity.
                return SettingsService.sharedValues.filteringOpacity; // Edit mode: transparent
              } else {
                return 0; // Read mode: completely hidden (invisible)
              }
            } else {
              return 1;
            }
          }
        }
      },
      // Additional edge styles would continue here...
      // For brevity, I'm including the most important ones
      {
        selector: 'edge[shape]',
        style: {
          //@ts-ignore
          'line-style': (element) => {
            const data = this.getLinkDataBasedOnState(element);
            return cytoscapeStrokeStyle(data.shape || DefaultValues.link.shape);
          },
          //@ts-ignore
          'line-dash-pattern': (element) => {
            const data = this.getLinkDataBasedOnState(element);
            return strokeDashPattern(
              data.shape || DefaultValues.link.shape,
              this.linkLineWidth(element)
            ) ?? [6, 3];
          },
          //@ts-ignore
          'line-cap': (element) => {
            const data = this.getLinkDataBasedOnState(element);
            const shape = data.shape || DefaultValues.link.shape;
            const arrowType = data.edgeArrowType || DefaultValues.link.edgeArrowType;
            const headless = data.direction === 'undirected' || arrowType === 'none';
            return headless && !strokeDashPattern(shape, 1) ? 'round' : strokeLineCap(shape);
          }
        }
      },
      {
        'selector': 'edge[label]',

        'style': {

          'label': (edge: any): any => {
            let name = '';
            const data = this.getLinkDataBasedOnState(edge);
            if (data?.label === 'name') {
              name = data.name || '';
            } else if (data.metadata && data.metadata.length > 0) {
              name = data.metadata?.find((meta: {
                key: string;
                value: string
              }) => meta.key === data.label)?.value || '';
            }
            const hasSomeUrlMetaValue = data.metadata?.some((m: any) => {
              return this.containsUrl(m.value);
            });
            if (hasSomeUrlMetaValue) {
              name = StatusIcons.HAS_LINKS + '\u00A0' + name;
            }
            if (data.hasComments && params.cyService.canSeeComments()) {
              name = name + '\u00A0' + StatusIcons.HAS_COMMENTS;
            }
            return name;
          },
          'text-valign': 'center',
          'text-halign': 'center',
          'font-family': 'Roboto, "maporium"',
          'background-color': '#fff',
          'text-margin-y': (edge: any) => {
            const angle = params.cyService.computeEdgeAngle(edge);
            const margin = params.cyService.computeMarginBasedOnAngle(angle);
            return margin.y;
          },
          'text-margin-x': (edge: any) => {
            const angle = params.cyService.computeEdgeAngle(edge);
            const margin = params.cyService.computeMarginBasedOnAngle(angle);
            return margin.x;
          },
          'text-rotation': (edge: any) => {
            const data = this.getLinkDataBasedOnState(edge);
            const labelSlope = data.labelSlope || DefaultValues.link.labelSlope;

            if (labelSlope === 'straight') {
              return 'none';
            } else if (labelSlope === 'sloped') {
              return 'autorotate';
            } else {
              // For 'curved' - keep as 'none' until curved functionality is implemented
              return 'none';
            }
          },
          'font-size': (edge: any) => {
            const data = this.getLinkDataBasedOnState(edge);

            const size = data.textFontSize;
            return size !== undefined && size !== null && size !== '' ? size : DefaultValues.link.textFontSize;
          },
          'color': (edge: any) => {
            const map = this.mapService.getSelectedMapSignal();
            const isDark = this.settingsService.isDarkMode(map?.styles?.appearance);
            return isDark ? '#FFFFFF' : '#000000';
          },
          'text-wrap': 'wrap'
        }
      },
      // Selection and interaction styles
      // Style when mouse button was released and the node is selected
      {
        selector: 'node:selected',
        style: {
          'overlay-opacity': 0,
          'outline-style': 'solid',
          'outline-width': (e: any) => this.selectionRingWidth(this.nodeMinorExtent(e), CytoscapeService.SELECTION_NODE_RING_SHARE),
          'outline-offset': () => (2 * CytoscapeService.SELECTION_GAP_PX) / (this.cy?.zoom() || 1),
          'outline-color': () => this.selectionRingColor(),
          'outline-opacity': (e: any) => this.selectionRingOpacity(e.id()),
          'z-index-compare': 'manual',
          'z-index': 2
        }
      },
      // This style is used when mouse button is pressed and hold
      {
        selector: 'node:active',
        style: {
          'overlay-opacity': 0,
          'z-index-compare': 'manual',
          'z-index': 2
        }
      },
      {
        selector: 'edge:active',
        style: {
          'overlay-padding': (e: any) => {
            const edgeData = this.getLinkDataBasedOnState(e);
            const weight = Number(edgeData?.weight) || 0;

            const basePadding = 2;
            const weightAdjustment = Math.min(weight / 2, 6); // Slightly smaller than selected
            const adjustedPadding = basePadding + weightAdjustment;

            return `${adjustedPadding}px`;
          },
          'overlay-opacity': () => SettingsService.sharedValues.node.nodeOverlaySelectOpacity,
          'overlay-color': () => {
            const map = this.mapService.getSelectedMapSignal();
            const isDark = this.settingsService.isDarkMode(map?.styles?.appearance);
            return isDark ? '#FFFFFF' : SettingsService.sharedValues.link.selectedOverlayColor;
          },
          'z-index-compare': 'manual',
          'z-index': 5
        }
      },
      {
        selector: 'edge:selected',
        style: {
          'z-index': -1,
          'z-index-compare': 'manual',
          'overlay-opacity': 0,
          'underlay-padding': (e: any) => {
            const width = Number(e.pstyle('width')?.pfValue) || 0;
            return width / 2 + this.selectionRingWidth(width, CytoscapeService.SELECTION_LINK_RING_SHARE);
          },
          'underlay-color': () => this.selectionRingColor(),
          'underlay-opacity': (e: any) => this.selectionRingOpacity(e.id())
        }
      },
      {
        selector: '.eh-ghost-edge',
        style: {
          'background-color': (edge: any) => {
            const data = this.getLinkDataBasedOnState(edge, DashboardComponent.tappedLink);
            return data?.color || DefaultValues.link.color;
          },
          'line-color': (edge: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(edge, DashboardComponent.tappedLink);
            return data?.color || DefaultValues.link.color;
          },
          'target-arrow-color': (edge: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(edge, DashboardComponent.tappedLink);
            return data?.color || DefaultValues.link.color;
          },
          'source-arrow-color': (edge: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(edge, DashboardComponent.tappedLink);
            return data?.color || DefaultValues.link.color;
          },
          'source-arrow-shape': () => {
            return params.componentInstance.isChangingLinkSource ? 'triangle' : 'none';
          },
          'line-style': (element: any) => {
            const data = this.getLinkDataBasedOnState(element, DashboardComponent.tappedLink);
            return data.shape || DefaultValues.link.shape;
          },
          'curve-style': (element: any) => {
            const data = this.getLinkDataBasedOnState(element, DashboardComponent.tappedLink);
            // @ts-ignore
            return EdgeTypeMapCy[data.type || 'BEZIER'] || 'bezier';
          },
          'arrow-scale': (link: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(link, DashboardComponent.tappedLink);
            const isGhostNode = Object.keys(data)?.length === 3;
            return data.weight as number >= 1 ? data.weight as number / 2 :
              isGhostNode ? 0.5 : 0.3;
          },
          'width': (link: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(link, DashboardComponent.tappedLink);
            const isGhostNode = Object.keys(data)?.length === 3;
            return data.weight as number >= 1 ?
              data.weight as number :
              isGhostNode ? 1 : 0.1;
          },
          'z-index-compare': 'manual',
          'z-index': 1,
          'target-arrow-shape': () => {
            return params.componentInstance.isChangingLinkSource ? 'none' : 'triangle';
          }
        }
      },
      {
        selector: '.eh-ghost-edge.eh-preview-active',
        style: {
          'z-index-compare': 'manual',
          'z-index': 1,
          'opacity': 0,
          'source-arrow-shape': () => {
            return params.componentInstance.isChangingLinkSource ? 'triangle' : 'none';
          },
          'target-arrow-shape': () => {
            return params.componentInstance.isChangingLinkSource ? 'none' : 'triangle';
          }
        }
      },
      {
        selector: '.eh-preview',
        style: {
          'source-arrow-shape': () => {
            return params.componentInstance.isChangingLinkSource ? 'triangle' : 'none';
          },
          'target-arrow-shape': () => {
            return params.componentInstance.isChangingLinkSource ? 'none' : 'triangle';
          },
          'line-color': (edge: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(edge, DashboardComponent.tappedLink);
            return data?.color || DefaultValues.link.color;
          },
          'target-arrow-color': (edge: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(edge, DashboardComponent.tappedLink);
            return data?.color || DefaultValues.link.color;
          },
          'source-arrow-color': (edge: EdgeSingular) => {
            const data = this.getLinkDataBasedOnState(edge, DashboardComponent.tappedLink);
            return data?.color || DefaultValues.link.color;
          }
        }
      }
    ];
  }

  private selectionFocusId(): string | null {
    return DashboardComponent.tappedNode?.id ?? DashboardComponent.tappedLink?.id ?? null;
  }

  private isSelectionFocus(id: string): boolean {
    return (this.cy?.$(':selected').length ?? 0) <= 1 || id === this.selectionFocusId();
  }

  private selectionRingWidth(size: number, share: number): number {
    const zoom = this.cy?.zoom() || 1;
    const base = CytoscapeService.SELECTION_RING_PX;
    const min = CytoscapeService.SELECTION_RING_MIN_PX;
    return Math.max(min / zoom, Math.min(base / zoom, share * Math.max(0, size)));
  }

  private selectionRingColor(): string {
    const map = this.mapService.getSelectedMapSignal();
    return this.settingsService.getAppearanceAwareColor('#000000', '#FFFFFF', map?.styles?.appearance);
  }

  private selectionRingOpacity(id: string): number {
    return this.isSelectionFocus(id) ? CytoscapeService.SELECTION_FOCUS_OPACITY : CytoscapeService.SELECTION_OTHERS_OPACITY;
  }

  private nodeMinorExtent(node: NodeSingular): number {
    return Math.min(Number(node.width()) || 0, Number(node.height()) || 0);
  }

  private installSelectionRefresh(cy: cytoscape.Core): void {
    this.selectionLook = { zoom: cy.zoom(), focusId: null, count: 0 };
    cy.on('render select unselect', () => this.queueSelectionRefresh(cy));
  }

  private queueSelectionRefresh(cy: cytoscape.Core): void {
    if (this.selectionRefreshQueued) return;
    this.selectionRefreshQueued = true;
    requestAnimationFrame(() => {
      this.selectionRefreshQueued = false;
      if (this.cy !== cy || cy.destroyed()) return;
      const selected = cy.$(':selected');
      const zoom = cy.zoom();
      const focusId = this.selectionFocusId();
      const last = this.selectionLook;
      const zoomMoved = last.zoom <= 0 || Math.abs(zoom / last.zoom - 1) > CytoscapeService.SELECTION_ZOOM_TOLERANCE;
      if (!zoomMoved && focusId === last.focusId && selected.length === last.count) return;
      this.selectionLook = { zoom, focusId, count: selected.length };
      if (selected.length > 0) (selected as unknown as { updateStyle: () => void }).updateStyle();
    });
  }

  private getAppearanceAwareCyNodeStyles() {
    return [
      {
        selector: 'node.highlighted',
        style: {
          'overlay-opacity': () => SettingsService.sharedValues.node.nodeDropOverlayOpacity,
          'overlay-padding': '2px',
          'overlay-color': () => {
            const map = this.mapService.getSelectedMapSignal();
            return this.settingsService.getAppearanceAwareColor(SettingsService.sharedValues.node.nodeHighlightColor, '#FFFFFF', map?.styles?.appearance);
          },
          'z-index-compare': 'manual',
          'z-index': 2
        }
      },
      {
        selector: 'node:parent.grabbed',
        style: {
          opacity: () => SettingsService.sharedValues.node.nodeDragOpacity,
          'z-index-compare': 'manual',
          'z-index': 2
        }
      },
      {
        selector: '.grabbed',
        style: {
          'overlay-opacity': () => SettingsService.sharedValues.node.nodeDragOpacity,
          'overlay-padding': '2px',
          'overlay-color': () => {
            const map = this.mapService.getSelectedMapSignal();
            return this.settingsService.getAppearanceAwareColor(SettingsService.sharedValues.node.nodeHighlightColor, '#FFFFFF', map?.styles?.appearance);
          },
          'z-index': 2,
          'z-index-compare': 'manual'
        }
      }
    ];
  }

  /**
   * Call this method when smap appearance changes to update label colors
   */
  public updateStylesForAppearanceChange(): void {
    if (!this.cy) return;
    this.updateHtmlEdgeLabelColors();
    this.updateEdgeEditingColors();
    this.cy.style().update();
    // cy.style().update() invalidates dynamic style getters but doesn't
    // always trigger a visible redraw — without forceRender the canvas
    // shows stale colors until the next user interaction. MAP-1113.
    this.cy.forceRender();
  }

  /** Labels read rscratch.allpts, which cytoscape refreshes on the next render. */
  public redrawLinkLabelsAfterRender(): void {
    const run = () => this.linkLabelRenderer?.redraw();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      setTimeout(run, 32);
    }
  }

  /** The plugin captures these colors in a closure at init. */
  public refreshEdgeEditingColors(): void {
    this.updateEdgeEditingColors();
  }

  // Refresh anchor/endpoint colors of the edge-editing plugin so curved-link
  // handles match the new appearance. The plugin captures these colors at
  // init in a closure, so we go through its public setter.
  private updateEdgeEditingColors(): void {
    if (!this.cy) return;
    const map = this.mapService.getSelectedMapSignal();
    const isDark = this.settingsService.isDarkMode(map?.styles?.appearance);
    const color = isDark ? '#ffffff' : '#000000';
    try {
      const edgeEditing = (this.cy as any).edgeEditing?.('get');
      edgeEditing?.updateAnchorColors?.(color, color);
    } catch {
      // plugin not initialized yet — colors will be correct on init
    }
  }

  /**
   * Optimized state switching - updates elements in-place without destroying/reloading the canvas.
   * Uses the embedded state data already present in each node/link's `states` array.
   */
  public async switchStateOptimized(
      cy: Core,
      newStateId: string | null,
      options: { animate?: boolean; animationDuration?: number; onComplete?: () => void } = {}
  ): Promise<void> {
    const {animate = true, animationDuration = 300, onComplete} = options;
    const expandCollapseApi = (cy as any).expandCollapse?.('get');
    const isDefaultState = newStateId === null || newStateId === undefined;

    // Collect position and collapse changes
    const positionChanges: Array<{ node: NodeSingular; pos: { x: number; y: number } }> = [];
    const collapseChanges: Array<{ node: NodeSingular; collapse: boolean }> = [];

    cy.nodes().forEach((node) => {
      const states = node.data('states');
      let targetPos: { x: number; y: number } | null = null;
      let targetCollapsed: boolean | null = null;

      if (isDefaultState) {
        // Default state - use base node position
        targetPos = {x: node.data('x') ?? node.position().x, y: node.data('y') ?? node.position().y};
        targetCollapsed = node.data('collapsed') ?? false;
      } else if (states) {
        const nodeState = states.find((s: NodeStateData) => s.stateId === newStateId);
        if (nodeState?.node) {
          targetPos = {x: nodeState.node.x, y: nodeState.node.y};
          targetCollapsed = nodeState.node.collapsed ?? false;
        } else {
          // No state entry for target state - fall back to base position
          targetPos = {x: node.data('x') ?? node.position().x, y: node.data('y') ?? node.position().y};
          targetCollapsed = node.data('collapsed') ?? false;
        }
      } else {
        // No states array - fall back to base position
        targetPos = {x: node.data('x') ?? node.position().x, y: node.data('y') ?? node.position().y};
        targetCollapsed = node.data('collapsed') ?? false;
      }

      if (targetPos && (targetPos.x !== node.position().x || targetPos.y !== node.position().y)) {
        positionChanges.push({node, pos: targetPos});
      }

      const currentCollapsed = node.hasClass('collapsed');
      if (targetCollapsed !== null && targetCollapsed !== currentCollapsed) {
        collapseChanges.push({node, collapse: targetCollapsed});
      }
    });

    // Execute collapse/expand changes FIRST (before position animation)
    for (const {node, collapse} of collapseChanges) {
      if (collapse && expandCollapseApi && node.data('children')?.length > 0) {
        expandCollapseApi.collapse(node);
        node.addClass('collapsed');
      } else if (!collapse && expandCollapseApi) {
        expandCollapseApi.expand(node);
        node.removeClass('collapsed');
      }
    }

    // Animate positions
    if (animate && positionChanges.length > 0) {
      await Promise.all(positionChanges.map(({node, pos}) =>
          new Promise<void>(resolve => {
            node.animate({position: pos}, {duration: animationDuration, easing: 'ease-out', complete: () => resolve()});
          })
      ));
    } else {
      positionChanges.forEach(({node, pos}) => node.position(pos));
    }

    // Reinitialize edge anchor points after positions have changed
    this.reinitializeEdgeAnchors(cy);

    // Refresh styles and labels
    cy.style().update();
    cy.forceRender();
    this.linkLabelRenderer?.clearClassCache?.();
    this.linkLabelRenderer?.redraw();

    onComplete?.();
  }

  /**
   * Reinitialize edge bend/control points after state change.
   * This ensures curved/bent edges display correctly at new positions.
   */
  private reinitializeEdgeAnchors(cy: Core): void {
    const edgeEditing = (cy as any).edgeEditing?.('get');
    if (!edgeEditing) return;

    cy.edges().forEach((edge) => {
      try {
        edgeEditing.initAnchorPoints([edge]);
      } catch {
        // Silently ignore initialization errors for individual edges
      }
    });
  }

}
